import { v4 as uuidv4 } from "uuid";
import { decrypt, decryptEntry, encrypt, encryptEntry,encryptManifestFile } from "../services/SecurityService";
import CryptoJS from "crypto-js/core";
import {getAWSUrl, getFromS3 , hasInternet } from '@/services/AWSService';
import {store} from "@/store";
import {getImageDataUri , formatDate} from "@/utilities/Compatibility";
import Worker from 'worker-loader!../background-worker/worker';
import {isConnectedToInternet} from '@/utilities/Network';
import {Device} from '@capacitor/device';
import {writeFileToDataDirectory,dataDirectoryFileExists,readJSONFileFromDataDirectory,writeFileToDocumentsDirectory,logInfo,logError , ensureFileFromAssets, readTextFileFromDataDirectory, isCachedFileValid, ensureImageFromAssets} from "@/utilities/Filesystem";
import {networkSpeedService} from '@/services/NetworkSpeedService';
import { MimeTypes } from "@/types/types";

const DB_NAME = "CalcApp";
const LANGUAGE_KEYS = ["language", "buildConfigs", "calculator", "application", "menu", "bigPic", "journeyHelpPages", "onboardingPages", "learnWhereToStartPages", "privacyPage", "coachPage", "securityPage"];
let DB: IDBDatabase;

// NOTE: Removed getDb() concurrency guard - it was causing deadlocks
// The circular dependency between getDb() and getMinRequiredDataForAppLoad() made it unsafe

const worker = new Worker();

// Default set worker throttling/queue state
const DEFAULT_SET_START_DELAY_MS = 5000;
const DEFAULT_SET_STEP_DELAY_MS = 3000;
const defaultSetQueue: string[] = [];
let defaultSetWorkerActive = false;
let defaultSetStartTimer: ReturnType<typeof setTimeout> | null = null;
let defaultSetNextTimer: ReturnType<typeof setTimeout> | null = null;
let defaultSetFetchUrl = '';
let defaultSetAppVersion = 0;
let defaultSetStartDelayMs = DEFAULT_SET_START_DELAY_MS;
let defaultSetStepDelayMs = DEFAULT_SET_STEP_DELAY_MS;

const normalizeDefaultSetId = (id: string) => id.toUpperCase();

const enqueueDefaultSetDownloads = (ids: string[], options: { startDelayMs?: number; stepDelayMs?: number; reason?: string } = {}) => {
    const normalized = ids.map(normalizeDefaultSetId);
    const newIds = normalized.filter((id) => !defaultSetQueue.includes(id));
    if (newIds.length === 0) {
        return;
    }
    defaultSetQueue.push(...newIds);
    if (options.startDelayMs) {
        defaultSetStartDelayMs = options.startDelayMs;
    }
    if (options.stepDelayMs) {
        defaultSetStepDelayMs = options.stepDelayMs;
    }
    logInfo(`[defaultSetQueue] Enqueued ${newIds.length} set(s)${options.reason ? ` (${options.reason})` : ''}`, 0);

    if (defaultSetWorkerActive || defaultSetStartTimer) {
        return;
    }
    defaultSetStartTimer = setTimeout(() => {
        startDefaultSetQueue().catch((error) => {
            logError(error, '[defaultSetQueue] Failed to start worker queue');
        });
    }, defaultSetStartDelayMs);
};

async function startDefaultSetQueue() {
    defaultSetStartTimer = null;
    if (defaultSetWorkerActive || defaultSetQueue.length === 0) {
        return;
    }
    defaultSetWorkerActive = true;
    defaultSetAppVersion = getDBVersion();
    defaultSetFetchUrl = getAWSUrl(await getRootLanguageFolder(), '');
    await sendNextDefaultSetToWorker();
}

function scheduleNextDefaultSet() {
    if (!defaultSetWorkerActive) {
        return;
    }
    if (defaultSetQueue.length === 0) {
        defaultSetWorkerActive = false;
        return;
    }
    if (defaultSetNextTimer) {
        clearTimeout(defaultSetNextTimer);
    }
    defaultSetNextTimer = setTimeout(() => {
        sendNextDefaultSetToWorker().catch((error) => {
            logError(error, '[defaultSetQueue] Failed to send next set');
        });
    }, defaultSetStepDelayMs);
}

async function sendNextDefaultSetToWorker() {
    if (defaultSetQueue.length === 0) {
        defaultSetWorkerActive = false;
        return;
    }
    const nextId = defaultSetQueue.shift();
    if (!nextId) {
        defaultSetWorkerActive = false;
        return;
    }
    logInfo(`[defaultSetQueue] Starting worker for set ${nextId}`, 0);
    callDefaultSetWorker([nextId], defaultSetAppVersion, defaultSetFetchUrl);
}

worker.addEventListener("message", (event: MessageEvent) => {
    store.dispatch("incrementBackgroundJourneySets",event.data);
    scheduleNextDefaultSet();
});

/**
 * Current index db version
 */
export function getDBVersion(): number {
    return DB?.version ?? 0;
}

/**
 * Decrypt and parse to json
 * @param toDecrypt an encrypted string
 */
export function decryptToJson(toDecrypt: string) {
    //console.log(`[decryptToJson] toDecrypt: ${toDecrypt}`);
    return JSON.parse(decrypt(toDecrypt).toString(CryptoJS.enc.Utf8));
}

/**
 * Empty db check for initialization
 * @param DB index db to check
 */
function needToLoadData(DB: any) {
    return new Promise((resolve) => {
        const trans = DB.transaction("media", "readonly");

        const store = trans.objectStore("media");
        const request = store.count();
        let returnValue: any;

        request.onsuccess = function() {
            returnValue = request.result === 0;
        };
        trans.oncomplete = () => {
            resolve(returnValue);
        };
    });
}

/**
 * Save defaults into db
 * @param storeName db store
 * @param object payload to save
 * @param DB db to save in
 */
function storeDefaults(storeName: string, object: Media | JourneySet | Quadrant | Journey | Language | string[], DB: any) {
    return new Promise<void>((resolve) => {
        const trans = DB.transaction([storeName], "readwrite");

        const store = trans.objectStore(storeName);
        if (store.autoIncrement)
            store.put(object);
        else
            store.put(object, 1);
        trans.oncomplete = () => {
            resolve();
        };
    });
}

/**
 * Read local file, returns json 
 * @param filePath local path to file
 */
async function readFile(filePath: string) {
    let file;
    try{
        file = await fetch(filePath);
    }catch (error){
        return undefined;
    }
    return await file.json();
}

/**
 * Empty live path reads local, is non-empty only when called from the first setLanguageFile.
 */

// Global flags to prevent concurrent downloads
let isLanguageFileDownloading = false;
let pendingLanguageFilePromise: Promise<any> | null = null;

let isManifestFileDownloading = false;
let pendingManifestFilePromise: Promise<any> | null = null;

let isAppVersionDownloading = false;
let pendingAppVersionPromise: Promise<string> | null = null;

async function getLanguageFileData() {
    // If a download is already in progress, wait for it instead of starting another
    if (isLanguageFileDownloading && pendingLanguageFilePromise) {
        logInfo('[getLanguageFileData] Download already in progress, waiting for completion...', 1);
        return pendingLanguageFilePromise;
    }
    
    // Mark as downloading and create the promise
    isLanguageFileDownloading = true;
    
    pendingLanguageFilePromise = (async () => {
        try {
            let livePath = '';
            //ensureFileFromAssets
            logInfo('[getLanguageFileData] Ensuring language.json from assets', 1);
            await ensureFileFromAssets('language.json' , 'assets/data/language.json');
            logInfo('[getLanguageFileData] Ensuring journeySetMetadata.json from assets', 1);
            await ensureFileFromAssets('journeySetMetadata.json' , 'assets/data/journeySetMetadata.json');
            const deviceInfo = await Device.getInfo();
            if (deviceInfo.platform === 'web') {
                livePath = 'language_encrypted_PWA.json';
            //store isn't set until after the 'local' language file read allowing for the initial load of the apps
            } else if(!store.state.isSecure) {
                if(deviceInfo.platform === "ios") {
                    livePath = 'language_encrypted_iOSAppStore.json';
                } else {
                    livePath = 'language_encrypted_androidAppStore.json'
                }
            } else if (store.getters.allowsInternetAccess() && store.state.secureUpdateOverride) {
                livePath = 'language_encrypted.json';
            }

            const languageFileData = await getFileData('language.json', livePath);
            return languageFileData;
        } finally {
            // Clear flags when download completes (success or failure)
            isLanguageFileDownloading = false;
            pendingLanguageFilePromise = null;
        }
    })();
    
    return pendingLanguageFilePromise;
}

/**
 * Get the language file with smart version checking
 * On app restart (not first time), checks S3 version before downloading
 * Checks local cached file version before downloading from S3
 * On first load, uses assets immediately and schedules background download
 */
async function getLanguageFileDataWithVersionCheck(isFirstTime: boolean) {
    const deviceInfo = await Device.getInfo();
    
    // For first time: optimize by skipping speed test and using assets + background download
    if (isFirstTime) {
        logInfo('[getLanguageFileDataWithVersionCheck] SPEED TEST: SKIPPED (first load) - Using assets immediately', 0);
        logInfo('[getLanguageFileDataWithVersionCheck] First time opening app, using assets and scheduling background download', 1);
        
        // For secure apps without network access, use bundled/local language only
        if (store.getters.isSecure() && !store.getters.allowsInternetAccess()) {
            return await getLanguageFileData();
        }
        
        // For non-secure mobile: use assets immediately, background download later
        if (deviceInfo.platform !== 'web' && !store.getters.isSecure()) {
            try {
                // Read from assets immediately - no network check needed
                logInfo('[getLanguageFileDataWithVersionCheck] Reading from assets for fast first load', 1);
                const assetData = await fetch('assets/data/language.json');
                const languageData = await assetData.json();
                
                // TEMPORARILY DISABLED: Background download disabled for debugging iPhone crash
                // The crash correlates strongly with network activity during initialization
                // Schedule background download (fire and forget)
                // const livePath = deviceInfo.platform === "ios" 
                //     ? 'language_encrypted_iOSAppStore.json' 
                //     : 'language_encrypted_androidAppStore.json';
                // downloadLanguageInBackground(livePath, true);
                logInfo('[getLanguageFileDataWithVersionCheck] BACKGROUND DOWNLOAD DISABLED FOR DEBUG', 0);
                
                return languageData;
            } catch (error: any) {
                logError(error, '[getLanguageFileDataWithVersionCheck] Error reading assets, falling back to standard download');
                return await getLanguageFileData();
            }
        }
        
        // For PWA, use standard logic
        return await getLanguageFileData();
    }
    
    // On restart when network access is allowed: check version first
    if (store.getters.allowsInternetAccess()) {
        try {
            // Step 1: Get the S3 version
            const s3Version = await getAppVersion();
            const s3VersionNum = parseInt(s3Version);
            const currentDBVersion = store.state.appVersion || 0;
            
            logInfo(`[getLanguageFileDataWithVersionCheck] S3 version: ${s3VersionNum}, DB version: ${currentDBVersion}`, 1);
            
            // Step 2: If S3 version is not newer, use local cache
            if (s3VersionNum <= currentDBVersion) {
                logInfo('[getLanguageFileDataWithVersionCheck] Local version is up to date, using cached file', 1);
                const localData = await readJSONFileFromDataDirectory('language.json', false);
                if (localData && Object.keys(localData).length > 0) {
                    return localData;
                }
            }
            
            // Step 3: S3 version is newer - check if local cached file has the right version
            if (deviceInfo.platform !== 'web') {
                const localData = await readJSONFileFromDataDirectory('language.json', false);
                if (localData && localData.appVersion && parseInt(localData.appVersion) === s3VersionNum) {
                    logInfo(`[getLanguageFileDataWithVersionCheck] Local cached file already has version ${s3VersionNum}, using it`, 1);
                    logInfo('[Loading] 40% - updating-language-from-local', 0);
                    store.dispatch("setLoadingValue", { value: 0.4, slug: "updating-language-from-local" });
                    // Update the DB version since we have the right file locally
                    await store.dispatch("setAppVersion", s3VersionNum);
                    return localData;
                }
            }
            
            // Step 4: Update needed - ALWAYS use cache + background download on restart
            // This prevents 3-minute stalls when speed test gives false positive
            logInfo('[getLanguageFileDataWithVersionCheck] SPEED TEST: SKIPPED (restart with update) - Using cache + background download to avoid stall', 0);
            logInfo(`[getLanguageFileDataWithVersionCheck] Update available (version ${s3VersionNum}), using cache and scheduling background download`, 1);
            
            // Use existing cached file immediately
            const localData = await readJSONFileFromDataDirectory('language.json', false);
            if (localData && Object.keys(localData).length > 0) {
                // TEMPORARILY DISABLED: Background download disabled for debugging iPhone crash
                // Schedule background download of new version
                // const livePath = deviceInfo.platform === "ios" 
                //     ? 'language_encrypted_iOSAppStore.json' 
                //     : 'language_encrypted_androidAppStore.json';
                // downloadLanguageInBackground(livePath, true);
                logInfo('[getLanguageFileDataWithVersionCheck] BACKGROUND DOWNLOAD DISABLED FOR DEBUG (restart)', 0);
                
                logInfo('[getLanguageFileDataWithVersionCheck] Using cached file, update will download in background', 1);
                return localData;
            }
            
            // No cached file available, must download (this is rare - only if cache was deleted)
            logInfo(`[getLanguageFileDataWithVersionCheck] No cached file, must download from S3`, 1);
            return await getLanguageFileData();
            
        } catch (error: any) {
            logError(error, '[getLanguageFileDataWithVersionCheck] Error in version check, falling back to local cache');
            const localData = await readJSONFileFromDataDirectory('language.json', false);
            if (localData && Object.keys(localData).length > 0) {
                return localData;
            }
            return await getLanguageFileData();
        }
    }
    
    // For secure apps with override, use standard logic
    return await getLanguageFileData();
}

/**
 * @param filePath local path to file
 * @param livePath aws file name, must always be '' if secure and not configured to allow internet
 * @param useHost Whether to use the application instance bucket (useHost = true) or Cloudfront Download Domain (useHost = false) 
 */
async function getFileData(filePath: string, livePath: string, useHost = true) {
    await store.dispatch('setPlatform');
    const deviceInfo = await Device.getInfo();
    let toReturn = {} as any;
    let mode = deviceInfo.platform === 'web' || (livePath !== '' && await isConnectedToInternet(false)) ? 'aws' : 'local';
    let shouldScheduleBackgroundDownload = false;
    
    //Offline app always needs to read local, also first load read local to initialize persisted variables
    logInfo(`[getFileData] Mode is ${mode} reading file: ${filePath} , Live file: ${livePath} `, 2) ;
    
    if (mode === 'aws'){
        const internetAlive = store.getters.isPwa() ? 'ok' : await hasInternet();
        //const internetAlive = 'ok';

        logInfo(`[getFileData] Internet is alive ${internetAlive}`,2);
        if(internetAlive !== 'ok'){
            mode = 'local';
        } else if (!store.getters.isPwa()) {
            // Check if connection is fast enough for 6MB language file download
            const shouldDownload = await networkSpeedService.shouldAttemptDownload('language');
            if (!shouldDownload) {
                logInfo('[getFileData] Connection too slow for 6MB language file, will use local cache and schedule background download', 1);
                mode = 'local';
                shouldScheduleBackgroundDownload = true; // Mark for background download
            }
        }
    }
    if (mode === 'aws' ){
        const rootLanguageFolder = await getRootLanguageFolder();
        
        try {
            // Get dynamic timeout based on network speed test (skip for PWA)
            const dynamicTimeout = store.getters.isPwa()
                ? 120000
                : await networkSpeedService.getRecommendedTimeout('language');
            const estimatedTime = store.getters.isPwa()
                ? null
                : networkSpeedService.getEstimatedDownloadTime('language');
            
            logInfo(`[getFileData] Using timeout: ${dynamicTimeout}ms for 6MB language file`, 1);
            if (estimatedTime) {
                logInfo(`[getFileData] Estimated download time: ${estimatedTime}s`, 1);
            }
            
            const fileData = await getFromS3(
                rootLanguageFolder, 
                `${livePath}?timestamp=${new Date().getTime()}`, 
                'json', 
                useHost,
                dynamicTimeout
            );
            
            toReturn = fileData.data;
            
            // Check if we got valid data
            if (!toReturn || Object.keys(toReturn).length === 0) {
                logInfo('[getFileData] Download failed or returned empty data, falling back to local', 1);
                mode = 'local';
            } else {
                // Success! Cache the file locally
                if (deviceInfo.platform !== 'web') {
                    await writeFileToDataDirectory('language.json', JSON.stringify(toReturn));
                }
                logInfo('[getFileData] Successfully downloaded and cached 6MB language file', 1);
            }
        } catch (error: any) {
            logError(error, '[getFileData] Error downloading from S3, using local');
            mode = 'local';
        }
    }
    
    if (mode === 'local') {
        logInfo("[getFileData] Reading local file: " + filePath,2);
        toReturn = (await readJSONFileFromDataDirectory(filePath , false)) as any;
        //console.log(`[getFileData] local file data keys: ${JSON.stringify(Object.keys(toReturn))}`);
        if (Object.keys(toReturn).length === 0) {
            logInfo('[getFileData] fallback to assets file: assets/data/' + filePath , 2);
            const file = await fetch('assets/data/' + filePath);
            toReturn = await file.json();
        }
        
        // TEMPORARILY DISABLED: Background download disabled for debugging iPhone crash
        // If we should schedule a background download and we're not secure
        // if (shouldScheduleBackgroundDownload && livePath && !store.getters.isSecure()) {
        //     downloadLanguageInBackground(livePath, useHost);
        // }
    }
    return toReturn;
}

/**
 * Download language file in the background (non-blocking)
 * Used when connection is too slow for foreground download
 */
async function downloadLanguageInBackground(livePath: string, useHost: boolean) {
    // Prevent starting a new download if one is already in progress
    if (isLanguageFileDownloading) {
        logInfo('[downloadLanguageInBackground] Language download already in progress, skipping', 1);
        return;
    }
    
    logInfo('[downloadLanguageInBackground] Starting background download of language file', 1);
    
    isLanguageFileDownloading = true;
    
    try {
        const rootLanguageFolder = await getRootLanguageFolder();
        const deviceInfo = await Device.getInfo();
        
        // Use a very long timeout for background download since user isn't waiting
        const fileData = await getFromS3(
            rootLanguageFolder, 
            `${livePath}?timestamp=${new Date().getTime()}`, 
            'json', 
            useHost,
            1800000 // 30 minute timeout for background download on very slow connections
        );
        
        if (fileData.data && Object.keys(fileData.data).length > 0) {
            if (deviceInfo.platform !== 'web') {
                await writeFileToDataDirectory('language.json', JSON.stringify(fileData.data));
                logInfo('[downloadLanguageInBackground] Successfully downloaded and cached language file', 1);
                logInfo('[downloadLanguageInBackground] File will be loaded into IndexedDB on next app restart', 1);
                // Note: App version will be updated when this file is loaded into IndexedDB on next restart
                // via getLanguageFileDataWithVersionCheck() -> setLanguageData() -> store.dispatch("setAppVersion")
            }
        } else {
            logInfo('[downloadLanguageInBackground] No valid data received', 1);
        }
    } catch (error: any) {
        logError(error, '[downloadLanguageInBackground] Background download failed');
    } finally {
        isLanguageFileDownloading = false;
    }
}

/**
 * Schedules a background download of the language file (native only).
 * Safe to call when an update is detected; deduplicates in-flight downloads.
 * Exported so LiveUpdateService.checkForLanguageUpdate can trigger the download
 * when the startup path had background download disabled (e.g. for debug).
 */
export async function scheduleLanguageDownloadInBackground(): Promise<void> {
    const deviceInfo = await Device.getInfo();
    if (deviceInfo.platform === 'web') {
        return;
    }
    const livePath = deviceInfo.platform === 'ios'
        ? 'language_encrypted_iOSAppStore.json'
        : 'language_encrypted_androidAppStore.json';
    downloadLanguageInBackground(livePath, true);
}

/**
 * Returns application instance aka. root language
 */
async function getRootLanguageFolder(): Promise<string> {
    const deviceInfo = await Device.getInfo();
    if (deviceInfo.platform === 'web') {
        // Web versions are always hosted on their 'root' path
        const host = window.location.hostname;
        return host === 'localhost' ? 'demopattern' : host.split(".")[0];
    } else {
        return store.state.variant;
    }
}

//Local file is always encrypted, secure app always reads local, non-secure uses un-encrypted aws file when online

async function getManifestFileDataFromLocal() {
    let manifestFileData: any;
    logInfo(`[getManifestFileDataFromLocal] getting manifest from local storage` , 2);
    const localFileExists = await dataDirectoryFileExists('journeySetMetadata.json');
    if(!localFileExists){
        logInfo(`[getManifestFileDataFromLocal] falling back to original manifest` , 2);
        manifestFileData = await readFile('./assets/data/journeySetMetadata.json');
        return parseEncryptedArray(manifestFileData.content);
    }else{
        try {
            logInfo(`[getManifestFileDataFromLocal] reading manifest from data directory` , 2);
            manifestFileData = await readJSONFileFromDataDirectory('journeySetMetadata.json',false);
            const manifestData = parseEncryptedArray(manifestFileData.content);
            return manifestData
        } catch (error: any) {
            logError(`[getManifestFileDataFromLocal] error reading manifest from data directory` );
            manifestFileData = await readFile('./assets/data/journeySetMetadata.json');
            return parseEncryptedArray(manifestFileData.content);
        }
    }
}

async function getManifestFileDataFromAWS() {
    // If already downloading, wait for that download
    if (isManifestFileDownloading && pendingManifestFilePromise) {
        logInfo('[getManifestFileDataFromAWS] Download already in progress, waiting...', 1);
        return pendingManifestFilePromise;
    }
    
    isManifestFileDownloading = true;
    
    pendingManifestFilePromise = (async () => {
        try {
    logInfo('[getManifestFileData] Reading manifest file from AWS', 2);
            
            // Check if connection is fast enough (skip for PWA - always use S3)
            if (!store.getters.isPwa()) {
                const shouldDownload = await networkSpeedService.shouldAttemptDownload('manifest');
                
                if (!shouldDownload) {
                    logInfo('[getManifestFileData] Connection too slow, will try background download and use local cache for now', 1);
                    
                    // Try to read local cache first
                    const localData = await getManifestFileDataFromLocal();
                    
                    // TEMPORARILY DISABLED: Background download disabled for debugging iPhone crash
                    // The crash correlates strongly with network activity
                    // downloadManifestInBackground();
                    
                    return localData;
                }
            }
    
    // Get dynamic timeout for 1.5MB manifest file
    const dynamicTimeout = await networkSpeedService.getRecommendedTimeout('manifest');
    logInfo(`[getManifestFileData] Using dynamic timeout: ${dynamicTimeout}ms for 1.5MB manifest file`, 1);
    
    const awsFileData = await getFromS3(
        await getRootLanguageFolder(), 
        'journeySetMetadata_encrypted.json', 
        'json', 
        false,
        dynamicTimeout
    );

    const deviceInfo = await Device.getInfo();
    if(deviceInfo.platform === 'web'){
        if (Array.isArray(awsFileData.data)) {
            return awsFileData.data;
        }
        return  parseEncryptedArray(awsFileData.data.content);
    }
            
            // Check if we got valid data
            if (!awsFileData.data) {
                logInfo('[getManifestFileData] No valid data from AWS, falling back to local', 1);
                return await getManifestFileDataFromLocal();
            }
            
    logInfo('[getManifestFileData] Writing manifest file to local data directory', 2);
    await writeFileToDataDirectory('journeySetMetadata.json', JSON.stringify(awsFileData.data));
    if (Array.isArray(awsFileData.data)) {
        return awsFileData.data;
    }
    return parseEncryptedArray(awsFileData.data.content);
        } finally {
            isManifestFileDownloading = false;
            pendingManifestFilePromise = null;
        }
    })();
    
    return pendingManifestFilePromise;
}

// Guard to prevent multiple simultaneous background manifest downloads
let isBackgroundManifestDownloading = false;

/**
 * Download manifest file in the background (non-blocking)
 * Called on app startup and when app gains focus
 * Downloads journeySetMetadata from S3 and updates local cache + database
 */
export async function downloadManifestInBackground(): Promise<void> {
    // Guard against multiple simultaneous downloads
    if (isBackgroundManifestDownloading) {
        logInfo('[downloadManifestInBackground] Already in progress, skipping', 1);
        return;
    }
    
    // Skip when app must not use the network (secure default or offline-only secure)
    if (!store.getters.allowsInternetAccess()) {
        logInfo('[downloadManifestInBackground] Skipping (no network access)', 1);
        return;
    }
    
    // Skip for PWA
    if (store.getters.isPwa()) {
        logInfo('[downloadManifestInBackground] Skipping for PWA', 1);
        return;
    }
    
    isBackgroundManifestDownloading = true;
    logInfo('[downloadManifestInBackground] Starting background download of manifest file', 1);
    
    try {
        // Check internet connectivity first (silently, no alert)
        const internetStatus = await isConnectedToInternet(false);
        if (!internetStatus) {
            logInfo('[downloadManifestInBackground] No internet connection, skipping', 1);
            return;
        }
        
        // Use a longer timeout for background download since user isn't waiting
        const awsFileData = await getFromS3(
            await getRootLanguageFolder(), 
            'journeySetMetadata_encrypted.json', 
            'json', 
            false,
            300000 // 5 minute timeout for background download
        );
        
        if (awsFileData.data) {
            const deviceInfo = await Device.getInfo();
            if (deviceInfo.platform !== 'web') {
                // Save the raw file to the data directory (same location as ensureFileFromAssets)
                await writeFileToDataDirectory('journeySetMetadata.json', JSON.stringify(awsFileData.data));
                logInfo('[downloadManifestInBackground] Successfully downloaded and cached manifest file', 1);
                
                // Parse and update the database with new journey set metadata
                const manifestData = Array.isArray(awsFileData.data)
                    ? awsFileData.data
                    : parseEncryptedArray(awsFileData.data.content);
                if (manifestData && manifestData.length > 0) {
                    // Remove obsolete sets that are no longer on S3
                    await removeObsoleteSets(manifestData);
                    // Save the new journey set metadata to the database
                    await saveJourneySetMetaData(manifestData);
                    logInfo(`[downloadManifestInBackground] Updated database with ${manifestData.length} journey sets`, 1);
                }
            }
        } else {
            logInfo('[downloadManifestInBackground] No valid data received', 1);
        }
    } catch (error: any) {
        logError(error, '[downloadManifestInBackground] Background download failed');
    } finally {
        isBackgroundManifestDownloading = false;
    }
}

export async function getManifestFileData() {
    if (store.getters.isPwa()) {
        return await getManifestFileDataFromAWS();
    }
    // mode = 'local' | 'aws' 
    let mode = 'local';
    
    if (store.getters.allowsInternetAccess() && await isConnectedToInternet(false)) {
        const internetAlive = store.getters.isPwa() ? 'ok' : await hasInternet();
        if(internetAlive === 'ok'){
            logInfo('[getManifestFileData] Online internet, retrieving manifest from AWS', 2);
            mode = 'aws';
        }else{
            logInfo(`[getManifestFileData] No internet(${internetAlive}), falling back to local file`, 2);
            mode = 'local';
        }
    }
    let manifestFileData;
    if( mode===`aws`){
        manifestFileData = await getManifestFileDataFromAWS();
        if(!manifestFileData || manifestFileData.length === 0){
            logInfo('[getManifestFileData] No manifest data from AWS, falling back to local file', 2);
            mode = 'local';
        }
    }
   
    if(mode === 'local'){
        manifestFileData = await getManifestFileDataFromLocal();
    }
    return manifestFileData;
}



async function initPreferences(config: any) {
    const preferences = store.getters.getPreferences();
    preferences.shouldDisplayInternetAlert = config.shareInternetWarning === "true";
    preferences.shouldDisplayUnencryptedAlert = config.shareUnencryptedWarning === "true";
    preferences.simpleCategoryShapes = config.simpleCategoryShapes === "true";
    preferences.logLevel = parseInt(config.logLevel, 10) || 2;
    preferences.allowAudioInnotes = config.allowAudioInnotes === "true";
    preferences.allowNotes = store.getters.isPwa() ? false : config.allowNotes === "true";
    await savePreferences(preferences);
    //await store.dispatch("setPreferences", preferences);
}

/**
 * Decryption helper for arrays
 * @param chunks array of encrypted strings 
 */
export function parseEncryptedArray(chunks: string[]) {
    let quadrantString = '';
    chunks.forEach((chunk: string) => {
        quadrantString += decryptToJson(chunk);
        });
    return JSON.parse(quadrantString);
}

/**
 * Saves meta data related to manifest file into the database and calls background worker for additional processing
 * @param metaData parsed journeySetMetadata.json Data
 * @param options configuration for background worker behavior
 */
async function saveJourneySetMetaData(
    metaData: any[],
    options: { skipDefaultSetQueue?: boolean } = {}
) {
    logInfo(`[saveJourneySetMetaData] START. Manifest count=${metaData.length}. isSecure=${store.getters.isSecure()} skipDefaultSetQueue=${options.skipDefaultSetQueue ?? false}`, 0);
    const defaultSets: string[] = [];
    const defaultSetTitles: string[] = [];
    const getPublishedTime = (value: any): number | null => {
        if (!value) {
            return null;
        }
        const time = new Date(value).getTime();
        return Number.isFinite(time) ? time : null;
    };
    for(const metaJourneySet of metaData) {
        const metaGuidRaw = metaJourneySet.guid;
        const metaGuidUpper = metaJourneySet.guid.toUpperCase();
        let journeySet: JourneySet | null = await getJourneySet(metaJourneySet.guid);
        const noJourneySet = !journeySet;
        logInfo(
            `[saveJourneySetMetaData] Meta guid raw="${metaGuidRaw}" upper="${metaGuidUpper}" quadrantId=${metaJourneySet.quadrantId ?? 'null'} IsSecureDefault=${metaJourneySet.IsSecureDefault} IsAppDefault=${metaJourneySet.IsAppDefault} IsPwaDefault=${metaJourneySet.IsPwaDefault} → lookup found=${!noJourneySet}${journeySet ? ` existingId="${journeySet.id}" existingQuadrantId=${journeySet.quadrantId ?? 'null'} existingAlreadyDownloaded=${journeySet.alreadyDownloaded} existingIsDefaultSet=${journeySet.isDefaultSet}` : ''}`,
            0
        );
        const existingPublishedTime = getPublishedTime(journeySet?.published);
        const metaPublishedTime = getPublishedTime(metaJourneySet.published);
        //legacy journey sets may not have published dates but should process correctly here as null === null after first load
        if(journeySet?.alreadyDownloaded &&
            (metaPublishedTime === null || (existingPublishedTime !== null && existingPublishedTime === metaPublishedTime))) {
            const orderChanged = journeySet.order !== metaJourneySet.order;
            if (journeySet.updateAvailable) {
                journeySet.updateAvailable = false;
                journeySet.published = metaJourneySet.published;
                await updateJourneySet(journeySet);
                logInfo(
                    `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: alreadyDownloaded+noPublishedChange → clear updateAvailable`,
                    0
                );
                logInfo(
                    `[saveJourneySetMetaData] Clear updateAvailable for ${metaJourneySet.guid} (no published change). ` +
                    `existing=${existingPublishedTime ?? 'null'}, meta=${metaPublishedTime ?? 'null'}`,
                    1
                );
            } else if (orderChanged) {
                journeySet.order = metaJourneySet.order;
                await updateJourneySet(journeySet);
                logInfo(
                    `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: alreadyDownloaded+noPublishedChange → update order only`,
                    0
                );
                logInfo(
                    `[saveJourneySetMetaData] Update order for ${metaJourneySet.guid} -> ${metaJourneySet.order}`,
                    1
                );
            } else {
                logInfo(
                    `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: alreadyDownloaded+noPublishedChange → skip (no update)`,
                    0
                );
                logInfo(
                    `[saveJourneySetMetaData] Skip ${metaJourneySet.guid} (already downloaded, no published change). ` +
                    `existing=${existingPublishedTime ?? 'null'}, meta=${metaPublishedTime ?? 'null'}`,
                    1
                );
            }
            continue;
        }
        
        if (!noJourneySet) {
            //Shortcut default sets that haven't completed loading. The published date will match in publishDiff below 
            // hit the already loaded logic but really the background worker needs to do the work for updating them
            if(journeySet.isDefaultSet && (!journeySet.deleted && !journeySet.alreadyDownloaded)) {
                defaultSets.push(journeySet.id);
                logInfo(
                    `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: default set pending load (alreadyDownloaded=false) → skip, queue for worker`,
                    0
                );
                logInfo(
                    `[saveJourneySetMetaData] Default set pending load ${metaJourneySet.guid} (alreadyDownloaded=false)`,
                    1
                );
                continue;
            }
            //legacy sets can also come across as "" instead of null, don't reload those either; don't re-install any
            //user deleted
            if(journeySet.deleted) {
                logInfo(`[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: deleted → skip`, 0);
                logInfo(
                    `[saveJourneySetMetaData] Skip ${metaJourneySet.guid} (deleted)`,
                    1
                );
                continue;
            }
            //Journeyset updated, auto load changes
            if (metaPublishedTime === null) {
                logInfo(`[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: meta published null → skip`, 0);
                logInfo(
                    `[saveJourneySetMetaData] Skip ${metaJourneySet.guid} (meta published null)`,
                    1
                );
                continue;
            }
            if (existingPublishedTime !== null && metaPublishedTime <= existingPublishedTime) {
                const orderChanged = journeySet.order !== metaJourneySet.order;
                if (journeySet.updateAvailable) {
                    journeySet.updateAvailable = false;
                    journeySet.published = metaJourneySet.published;
                    await updateJourneySet(journeySet);
                    logInfo(
                        `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: meta not newer → clear updateAvailable only`,
                        0
                    );
                    logInfo(
                        `[saveJourneySetMetaData] Clear updateAvailable for ${metaJourneySet.guid} (meta not newer). ` +
                        `existing=${existingPublishedTime}, meta=${metaPublishedTime}`,
                        1
                    );
                } else if (orderChanged) {
                    journeySet.order = metaJourneySet.order;
                    await updateJourneySet(journeySet);
                    logInfo(
                        `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: meta not newer → update order only`,
                        0
                    );
                    logInfo(
                        `[saveJourneySetMetaData] Update order for ${metaJourneySet.guid} -> ${metaJourneySet.order}`,
                        1
                    );
                } else {
                    logInfo(
                        `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: meta not newer → skip`,
                        0
                    );
                    logInfo(
                        `[saveJourneySetMetaData] Skip ${metaJourneySet.guid} (meta not newer). ` +
                        `existing=${existingPublishedTime}, meta=${metaPublishedTime}`,
                        1
                    );
                }
                continue;
            }
        }

        const isDefault = (metaJourneySet.IsSecureDefault && store.getters.isSecure() ||
            metaJourneySet.IsAppDefault && store.getters.isOnlineApp() ||
            metaJourneySet.IsPwaDefault && store.getters.isPwa());
        
        const alreadyDownloaded = journeySet?.alreadyDownloaded ?? false;
        const updateAvailable = alreadyDownloaded && metaPublishedTime !== null &&
            (existingPublishedTime === null || metaPublishedTime > existingPublishedTime);
        const existingQuadrantIdBeforeUpdate = journeySet?.quadrantId ?? null;
        logInfo(
            `[saveJourneySetMetaData] ${metaGuidUpper} BRANCH: create/update record. isDefault=${isDefault} alreadyDownloaded=${alreadyDownloaded} updateAvailable=${updateAvailable} metaQuadrantId=${metaJourneySet.quadrantId ?? 'null'} noJourneySet=${noJourneySet} existingQuadrantId=${existingQuadrantIdBeforeUpdate ?? 'null'}`,
            0
        );
        logInfo(
            `[saveJourneySetMetaData] ${metaJourneySet.guid} updateAvailable=${updateAvailable} ` +
            `alreadyDownloaded=${alreadyDownloaded} existing=${existingPublishedTime ?? 'null'} meta=${metaPublishedTime ?? 'null'}`,
            1
        );
        journeySet = {
            alreadyDownloaded: alreadyDownloaded,
            iconId: '',
            id: metaJourneySet.guid.toUpperCase(),
            isDefaultSet: isDefault,
            name: metaJourneySet.setname,
            numberOfJourneys: metaJourneySet.numberofjourneys,
            order: metaJourneySet.order,
            pdfName: '',
            quadrantId: metaJourneySet.quadrantId,
            tabContentImages: [],
            tabImages: [],
            updateAvailable: updateAvailable,
            published: metaJourneySet.published,
            deleted: noJourneySet ? false : (journeySet?.deleted ?? false),
            unavailableToDownload: false,
        };
        //Could further reduce space utilization on journeyset manifest icon changes by doing a delta check here
        if (metaJourneySet.icon) {
            journeySet.iconId = uuidv4();
            await saveMedia({ id: journeySet.iconId, content: metaJourneySet.icon || "" });
        }

        if(noJourneySet) {
            logInfo(`[saveJourneySetMetaData] ${metaGuidUpper} WRITE: saveJourneySet (new) id="${journeySet.id}" quadrantId=${journeySet.quadrantId ?? 'null'}`, 0);
            await saveJourneySet(journeySet);
        } else {
            logInfo(`[saveJourneySetMetaData] ${metaGuidUpper} WRITE: updateJourneySet (existing) id="${journeySet.id}" quadrantId=${journeySet.quadrantId ?? 'null'} (previous existingQuadrantId=${existingQuadrantIdBeforeUpdate ?? 'null'})`, 0);
            await updateJourneySet(journeySet);
        }
        
        if(isDefault) {
            defaultSets.push(journeySet.id);
            defaultSetTitles.push(journeySet.name);
        }
    }
    if(defaultSets.length > 0) {
        if (options.skipDefaultSetQueue) {
            logInfo(`[saveJourneySetMetaData] Skipping default set queue (skipDefaultSetQueue=true)`, 0);
            return;
        }
        logInfo(`[saveJourneySetMetaData] Queuing default set worker for ${defaultSets.length} default sets: ${defaultSetTitles.join(', ')}`, 0);
        enqueueDefaultSetDownloads(defaultSets, {
            reason: 'manifest-save',
            startDelayMs: 6000,
            stepDelayMs: 3000
        });
    } else {
        logInfo(`[saveJourneySetMetaData] No default sets to call worker for`, 2);
    }
}

/**
 * Posts a message to background worker.ts file 
 * @param defaultSets Sets to load in the background
 * @param appVersion Current version of the database, required for synchronization
 * @param fetchUrl location to pull files from if online
 */
function callDefaultSetWorker(defaultSets: string[], appVersion: number, fetchUrl: string) {
    const payload = JSON.stringify({appVersion, fetchUrl, defaultSets, readFromAWS: !store.state.isSecure});
    worker.postMessage(payload);
}

/**
 * Check for incomplete default journey sets for a specific field/quadrant and restart worker if needed
 * This handles cases where downloads were interrupted (e.g., by bundle updates, app crashes, etc.)
 * @param fieldId - The field/quadrant ID to check for incomplete default sets
 */
export async function checkAndRestartIncompleteDownloadsForField(
    fieldId: string,
    options: { startDelayMs?: number; stepDelayMs?: number } = {}
): Promise<void> {
    const journeySets = await getJourneySets(false, fieldId);
    const incompleteDefaults: string[] = [];
    logInfo(`[checkAndRestartIncompleteDownloadsForField] Checking field ${fieldId} for incomplete default sets`, 0);
    
    for (const set of journeySets) {
        if (set.isDefaultSet && !set.alreadyDownloaded && !set.deleted) {
            logInfo(`[checkAndRestartIncompleteDownloadsForField] Found incomplete default set: ${set.name} (${set.id})`, 0);
            incompleteDefaults.push(set.id);
        }
    }
    
    if (incompleteDefaults.length > 0) {
        logInfo(`[checkAndRestartIncompleteDownloadsForField] Queuing worker for ${incompleteDefaults.length} incomplete default set(s)`, 0);
        enqueueDefaultSetDownloads(incompleteDefaults, {
            reason: `field-${fieldId}`,
            startDelayMs: options.startDelayMs ?? 4000,
            stepDelayMs: options.stepDelayMs ?? 3000
        });
    } else {
        logInfo(`[checkAndRestartIncompleteDownloadsForField] All default sets for field ${fieldId} are complete`, 0);
    }
}

/**
 * Check for incomplete default journey sets and restart worker if needed
 * This handles cases where downloads were interrupted (e.g., by bundle updates, app crashes, etc.)
 * Should be called after app initialization to ensure all default sets are downloaded
 */
export async function checkAndRestartIncompleteDownloads(): Promise<void> {
    const journeySets = await getJourneySets(false, '');
    const incompleteDefaults: string[] = [];
    console.log("[debug] checkAndRestartIncompleteDownloads:");
    for (const set of journeySets) {
        if (set.isDefaultSet && !set.alreadyDownloaded && !set.deleted) {
            console.log("[debug] checkAndRestartIncompleteDownloads: incomplete set: " + set.name);
            incompleteDefaults.push(set.id);
        } else if (set.isDefaultSet && set.alreadyDownloaded) {
            console.log("[debug] checkAndRestartIncompleteDownloads: complete set: " + set.name);
        }
    }
    
    if (incompleteDefaults.length > 0) {
        enqueueDefaultSetDownloads(incompleteDefaults, {
            reason: 'global-check',
            startDelayMs: 5000,
            stepDelayMs: 3000
        });
    }
}

// Concurrency guard for getMinRequiredDataForAppLoad
let minDataLoadInProgress = false;
let minDataLoadPromise: Promise<void> | null = null;

/**
 * Retrieve and set the minimum data set required for application load
 * @param languageFileData parsed langauge.json file to process
 */
export async function getMinRequiredDataForAppLoad(languageFileData = {}): Promise<void> {
    // Check if min required data is already loaded
    if (Object.keys(store.getters.getMinRequiredLoadData()).length > 0) {
        logInfo('[getMinRequiredDataForAppLoad] Already loaded, skipping', 1);
        return;
    }
    
    // If loading is in progress, wait for it
    if (minDataLoadInProgress && minDataLoadPromise) {
        logInfo('[getMinRequiredDataForAppLoad] Loading in progress, waiting...', 1);
        return minDataLoadPromise;
    }
    
    // Set the guard
    minDataLoadInProgress = true;
    logInfo('[getMinRequiredDataForAppLoad] Starting (guard set)', 1);
    
    minDataLoadPromise = (async () => {
        try {
            await doGetMinRequiredDataForAppLoad(languageFileData);
        } finally {
            minDataLoadInProgress = false;
            minDataLoadPromise = null;
        }
    })();
    
    return minDataLoadPromise;
}

/**
 * Internal implementation of getMinRequiredDataForAppLoad
 */
async function doGetMinRequiredDataForAppLoad(languageFileData = {}): Promise<void> {
    console.log('[doGetMinRequiredDataForAppLoad] START');
    const firstLoad = Object.keys(languageFileData).length === 0;
    const isFirstTimeOpeningApp = store.getters.isFirstTimeOpeningApp();
    console.log('[doGetMinRequiredDataForAppLoad] firstLoad=', firstLoad, 'isFirstTimeOpeningApp=', isFirstTimeOpeningApp);
    
    // Check if this is a reload after bundle update
    // Use filesystem since localStorage/sessionStorage don't persist through LiveUpdate.reload()
    // NOTE: Don't delete the flag here - main.ts will delete it after all post-bundle logic completes
    let bundleJustUpdated = false;
    try {
        const flagContent = await readTextFileFromDataDirectory('bundleJustUpdated.flag');
        bundleJustUpdated = flagContent === 'true';
        if (bundleJustUpdated) {
            logInfo("[getMinRequiredDataForAppLoad] Detected post-bundle-update reload", 0);
        }
    } catch (error) {
        // Flag file doesn't exist, not a post-bundle-update reload
        bundleJustUpdated = false;
    }
    
    logInfo("Debug (50%): getMinRequiredDataForAppLoad " , 2);
    logInfo(`[getMinRequiredDataForAppLoad] firstLoad: ${firstLoad}, isFirstTimeOpeningApp: ${isFirstTimeOpeningApp}, bundleJustUpdated: ${bundleJustUpdated}`, 1);
    
    logInfo('[Loading] 40% - get-min-data-start', 0);
    store.dispatch("setLoadingValue", { value: 0.4 , slug: "get-min-data-start" });
    let languageData = {};
    
    if(firstLoad){
        logInfo("Debug: getMinRequiredDataForAppLoad , firstload getting language data ",1);
        
        // After a bundle update, skip version checks and just read the cached language file
        if (bundleJustUpdated) {
            logInfo("[getMinRequiredDataForAppLoad] Post-bundle-update: reading cached language file without version checks", 0);
            // Bundle is generic (no language files), so read from Directory.Data where it was cached
            const deviceInfo = await Device.getInfo();
            if (deviceInfo.platform !== 'web') {
                try {
                    const localData = await readJSONFileFromDataDirectory('language.json', false);
                    if (localData && Object.keys(localData).length > 0) {
                        logInfo("[getMinRequiredDataForAppLoad] Successfully read cached language file from Directory.Data", 1);
                        languageData = localData;
                    } else {
                        // Should not happen - language file should always be cached
                        logError(new Error('Language file not found in Directory.Data after bundle update'), 
                                '[getMinRequiredDataForAppLoad] This should not happen');
                        // Last resort: use standard download logic
                        languageData = await getLanguageFileData();
                    }
                } catch (error: any) {
                    logError(error, '[getMinRequiredDataForAppLoad] Error reading cached language file after bundle update');
                    // Last resort: use standard download logic
                    languageData = await getLanguageFileData();
                }
            } else {
                languageData = await getLanguageFileData();
            }
        } else {
            // Normal flow: Use the version-aware function that checks versions before downloading
            // After a bundle update, treat as non-first-time to skip asset copying
            languageData = await getLanguageFileDataWithVersionCheck(isFirstTimeOpeningApp && !bundleJustUpdated);
        }
        
        logInfo("Debug: getMinRequiredDataForAppLoad , firstload setting language data ",1);
        logInfo('[Loading] 40% - language-data-retrieved', 0);
        store.dispatch("setLoadingValue", { value: 0.4 , slug: "language-data-retrieved" });
        await setLanguageData(languageData, true);
    }else{
        languageData = languageFileData;
        logInfo("Debug: getMinRequiredDataForAppLoad setting languageData. " , 1);
        await setLanguageData(languageData, false);
    }
    logInfo("Debug: getMinRequiredDataForAppLoad , setLanguageData complete",2);
    logInfo('[getMinRequiredDataForAppLoad] Completed successfully', 0);
}

/**
 * Used to update the language file for mobile apps after initialization
 */
export async function reloadLanguage(): Promise<void> {
    // Secure without network access and PWA use language from init path only
    if (!store.getters.allowsInternetAccess() || store.getters.isPwa()) {
        return;
    }
    logInfo("Debug: reloadLanguage , reloading language file", 2);
    const languageData = await getLanguageFileData();
    if(languageData){
        // Skip loading indicators since this is a background update
        await setLanguageData(languageData, false, true);
    }
}

/**
 * 
 * @param languageData
 * @param overridePreferences
 * @param skipLoadingIndicators - Set to true when updating in background to avoid resetting loading bar
 */
async function setLanguageData(languageData: any, overridePreferences: boolean, skipLoadingIndicators: boolean = false) {
    let { locale } = languageData;
    const { content } = languageData;
    logInfo("Debug: setLanguageData", 2);
    if (Object.keys(languageData).includes('appVersion')) {
        logInfo("Debug: setLanguageData , setting app version from languageData : " + languageData.appVersion,1);
        await store.dispatch("setAppVersion", languageData.appVersion);
    }
    
    if (!skipLoadingIndicators) {
        logInfo('[Loading] 60% - parse-quadrants-start', 0);
        store.dispatch("setLoadingValue", { value: 0.6, slug: "parse-quadrants-start" });
    }
    logInfo("Debug (60%): setLanguageData , preparing to parse quadrants",2);
    const quadrants = parseEncryptedArray(content);
    
    if (!skipLoadingIndicators) {
        logInfo('[Loading] 70% - parse-locale-start', 0);
        store.dispatch("setLoadingValue", {value: 0.7, slug: "parse-locale-start"});
    }
    logInfo("Debug (70%): setLanguageData , preparing to parse locale",2);
    locale = decryptToJson(locale);
    const buildConfigs = locale[Object.keys(locale)[0]].buildConfigs;
    buildConfigs.config = {};
    if (store.getters.isPwa()) {
        Object.assign(buildConfigs.config, buildConfigs.web);
    }
    else {
        Object.assign(buildConfigs.config, buildConfigs.mobile);
    }
    logInfo("Debug: setLanguageData , setting locale and quadrants",1);
    await store.dispatch("setMinRequiredLoadData", { locale: locale, quadrants: quadrants});
    if(overridePreferences) {
        await initPreferences(buildConfigs.config);
    }
}

/**
 * Index DB loader
 * @param locale parsed from language file
 * @param quadrants Base quadrants for the application home page
 * @param appVersion db version
 */
async function loadDBInBackground(locale: any, quadrants: Quadrant[], appVersion: string) {
    const isDBEmpty = await needToLoadData(DB);
    logInfo(`[loadDBInBackground] isDBEmpty: ${isDBEmpty}`, 2);
    if (isDBEmpty) {
        await storeDefaults("recentJourneys", [], DB);
        await storeDefaults("fileUris", [], DB);
        //get and set journey quadrants
        logInfo(`[loadDBInBackground] getting quadrants`, 2);
        quadrants.forEach((quadrant: Quadrant) => {
            let quadrantIconId = "";
            const useAltIcon = (quadrant.id === 'jf5' || quadrant.id === 'jf6') && store.getters.showAllQuadrants();
            if (quadrant.icon) {
                quadrantIconId = uuidv4();
                storeDefaults("media", encryptEntry({id: quadrantIconId, content: useAltIcon ? quadrant.altIcon : quadrant.icon}), DB);
            }
            
            storeDefaults("quadrant",
                encryptEntry({
                    id: quadrant.id,
                    name: quadrant.name,
                    altName: quadrant.altName,
                    iconId: quadrantIconId,
                }), DB
            );
        });

        // load default language
        logInfo(`[loadDBInBackground] setting default language data`, 2);
        //console.log("locale: ", locale);
        for (const key of Object.keys(locale)) {
            const value = locale[key];
            delete value.buildConfigs.mobile;
            delete value.buildConfigs.web;
            for (const subKey of LANGUAGE_KEYS) {
                if (subKey === "language") {
                    await storeDefaults(subKey, encrypt(JSON.stringify({
                        locale: key,
                        messages: {language: value[subKey]}
                    })).toString(), DB);
                } else if (subKey === "application") {
                    value[subKey].appVersion = appVersion.toString();
                    await storeDefaults(subKey, encrypt(JSON.stringify(value[subKey])).toString(), DB);
                } else {
                    await storeDefaults(subKey, encrypt(JSON.stringify(value[subKey])).toString(), DB);
                }
            }
        }
        // Use LOCAL-ONLY manifest loading to avoid network-related crashes on iPhone
        // PWA should always load from S3
        if (store.getters.isPwa()) {
            await loadManifestMetaData();
        } else {
            // This loads from the bundled assets without any network calls
            await loadManifestMetaDataLocalOnly();
        }
    }else{
        logInfo(`[loadDBInBackground] DB already loaded`, 2);
        if( store.state.versionUpdated == true){
            logInfo(`[loadDBInBackground] version updated, updating DB`, 2);
            //update quadrants , fist remove existing media ids
            const existingQuadrants = (await getAllObjects("quadrant" , "fields" , true)) as Quadrant[];
            existingQuadrants.forEach((quadrant: Quadrant) => {
                if(quadrant.iconId){  
                    deleteMedia(quadrant.iconId)
                }
            });
            quadrants.forEach((quadrant: Quadrant) => {
                let quadrantIconId = "";
                const useAltIcon = (quadrant.id === 'jf5' || quadrant.id === 'jf6') && store.getters.showAllQuadrants();
                if (quadrant.icon) {
                    quadrantIconId = uuidv4();
                    storeDefaults("media", encryptEntry({id: quadrantIconId, content: useAltIcon ? quadrant.altIcon : quadrant.icon}), DB);
                }
                storeDefaults("quadrant",
                    encryptEntry({
                        id: quadrant.id,
                        name: quadrant.name,
                        altName: quadrant.altName,
                        iconId: quadrantIconId,
                    }), DB
                );
            });
            const preferences = await getPreferences();
            //This may need to change if we add other language choices
            const { isRelease } = locale[Object.keys(locale)[0]].language;
            preferences.isRelease = isRelease === "true";
            store.state.isRelease = preferences.isRelease;
            const config = store.getters.isPwa() ? locale[Object.keys(locale)[0]].buildConfigs.web : locale[Object.keys(locale)[0]].buildConfigs.mobile;
            preferences.simpleCategoryShapes = locale[Object.keys(locale)[0]].buildConfigs.config.simpleCategoryShapes === "true";
            preferences.logLevel = parseInt(locale[Object.keys(locale)[0]].buildConfigs.config.logLevel, 10) || 2;
            preferences.allowAudioInnotes = locale[Object.keys(locale)[0]].buildConfigs.config.allowAudioInnotes === "true";
            preferences.allowNotes = store.getters.isPwa() ? false : locale[Object.keys(locale)[0]].buildConfigs.config.allowNotes === "true";
            await savePreferences(preferences);
            await store.dispatch("setPreferences",preferences);
        } 
        
        store.dispatch("setVersionUpdated" , false);
    }
}

export async function reloadManifestMetaData() {
    // Use background download to avoid blocking UI and causing WebView crashes
    // The download runs asynchronously and updates the local cache + database when complete
    logInfo('[reloadManifestMetaData] Triggering background manifest download', 1);
    // Fire and forget - don't await to avoid blocking
    downloadManifestInBackground().catch(err => {
        logError(err, '[reloadManifestMetaData] Background download error');
    });
}

async function loadManifestMetaData() {
    const manifest = await getManifestFileData();
    // Always delete undownloaded sets that are no longer available on S3 from DB
    // This ensures unpublished journey sets are removed from the UI
    if (store.getters.allowsInternetAccess()) {
        await removeObsoleteSets(manifest);
    }
    await saveJourneySetMetaData(manifest);
}

/**
 * Load manifest metadata from LOCAL files only (no network)
 * Used during initial app load to avoid network-related crashes
 * Also used for crash recovery to restore any missing journey sets
 */
export async function loadManifestMetaDataLocalOnly(
    options: { skipDefaultSetQueue?: boolean } = {}
) {
    logInfo('[loadManifestMetaDataLocalOnly] Loading manifest from local files only', 0);
    const manifest = await getManifestFileDataFromLocal();
    if (manifest && manifest.length > 0) {
        await saveJourneySetMetaData(manifest, options);
        logInfo(`[loadManifestMetaDataLocalOnly] Loaded ${manifest.length} journey sets from local manifest`, 0);
    } else {
        logInfo('[loadManifestMetaDataLocalOnly] No local manifest data found', 0);
    }
}

async function removeObsoleteSets(currentManifest: any[]){
    const availableForDownload: string[] = [];
    const setsInDB = await getJourneySets(true);
    for(const metaJourneySet of currentManifest) {
        availableForDownload.push(metaJourneySet.guid.toUpperCase());
    }
    logInfo(`[removeObsoleteSets] Manifest has ${currentManifest.length} sets. GUIDs (uppercased): ${availableForDownload.join(', ')}`, 0);
    logInfo(`[removeObsoleteSets] DB has ${setsInDB.length} sets. IDs as stored: ${setsInDB.map((s: JourneySet) => `"${s.id}"`).join(', ')}`, 0);
    for (const journeySet of setsInDB) {
        const idUpper = journeySet.id.toUpperCase();
        const inManifest = availableForDownload.includes(idUpper);
        const wouldDelete = !inManifest && !journeySet.alreadyDownloaded;
        logInfo(
            `[removeObsoleteSets] Set id="${journeySet.id}" (upper="${idUpper}") quadrantId=${journeySet.quadrantId ?? 'null'} alreadyDownloaded=${journeySet.alreadyDownloaded} inManifest=${inManifest} wouldDelete=${wouldDelete}`,
            0
        );
        if (wouldDelete) {
            logInfo(`[removeObsoleteSets] Deleting set id="${journeySet.id}" (not in manifest, not alreadyDownloaded)`, 0);
            deleteJourneySet(journeySet.id, false);
        }
    }
}

export async function applyManifestUpdate(
    manifestData: any[],
    options: { skipDefaultSetQueue?: boolean } = {}
): Promise<void> {
    logInfo(`[applyManifestUpdate] ENTRY. manifestData length=${manifestData?.length ?? 0}`, 0);
    if (!manifestData || manifestData.length === 0) {
        logInfo('[applyManifestUpdate] No manifest data, returning', 0);
        return;
    }
    // Always remove undownloaded sets no longer listed
    await removeObsoleteSets(manifestData);
    await saveJourneySetMetaData(manifestData, options);
    logInfo('[applyManifestUpdate] DONE', 0);
}

export async function checkAndUpdateManifestWithSpeedCheck(
    options: { skipDefaultSetQueue?: boolean; skipSpeedCheck?: boolean } = {}
): Promise<void> {
    if (store.getters.isPwa()) {
        const manifestData = await getManifestFileDataFromAWS();
        if (manifestData && manifestData.length > 0) {
            await applyManifestUpdate(manifestData, options);
            logInfo(`[checkAndUpdateManifestWithSpeedCheck] Applied manifest update (PWA, ${manifestData.length} sets)`, 1);
        }
        return;
    }
    if (!store.getters.allowsInternetAccess()) {
        logInfo('[checkAndUpdateManifestWithSpeedCheck] Skipping (no network access)', 1);
        return;
    }
    if (!options.skipSpeedCheck) {
        const shouldDownload = await networkSpeedService.shouldAttemptDownload('manifest');
        if (!shouldDownload) {
            logInfo('[checkAndUpdateManifestWithSpeedCheck] Connection too slow, downloading in background', 1);
            downloadManifestInBackground().catch((error) => {
                logError(error, '[checkAndUpdateManifestWithSpeedCheck] Background manifest download failed');
            });
            return;
        }
    } else {
        logInfo('[checkAndUpdateManifestWithSpeedCheck] Skipping speed check (user-initiated secure check)', 0);
    }
    const manifestData = await getManifestFileDataFromAWS();
    if (manifestData && manifestData.length > 0) {
        await applyManifestUpdate(manifestData, options);
        logInfo(`[checkAndUpdateManifestWithSpeedCheck] Applied manifest update (${manifestData.length} sets)`, 1);
    }
}

export async function getLocalManifestJourneySetIds(): Promise<string[]> {
    const metaData = await getManifestFileData();
    const ids = [];
    for(const metaJourneySet of metaData) {
        ids.push(metaJourneySet.guid.toUpperCase());
    }
    return ids;
}

// Only call from live side of get file
// NEVER cache this file - it's tiny (10 bytes) and must always be fresh to check for updates
async function getAppVersion(): Promise<string> {
    logInfo('[getAppVersion] Called - determining variant and downloading appVersion.txt', 0);
    
    // Secure app without network access: return local version only.
    if (!store.getters.allowsInternetAccess()) {
        const currentVersion = store.state.appVersion ?? 0;
        logInfo(`[getAppVersion] Secure app - using local version only, no network: ${currentVersion}`, 0);
        return String(currentVersion);
    }
    
    // If already downloading, wait for that download
    if (isAppVersionDownloading && pendingAppVersionPromise) {
        logInfo('[getAppVersion] Download already in progress, waiting...', 0);
        return pendingAppVersionPromise;
    }
    
    isAppVersionDownloading = true;
    
    pendingAppVersionPromise = (async () => {
        try {
            const deviceInfo = await Device.getInfo();
            let rootLanguageFolder: string;
            
            // For web/PWA: use hostname
            if (deviceInfo.platform === 'web') {
    const host = window.location.hostname;
                rootLanguageFolder = host === 'localhost' ? 'demopattern' : host.split(".")[0];
                logInfo(`[getAppVersion] Web platform detected, using hostname-based variant: ${rootLanguageFolder}`, 0);
            } else {
                // For mobile: use store.state.variant, or get it from local language.json
                if (store.state.variant) {
                    rootLanguageFolder = store.state.variant;
                    logInfo(`[getAppVersion] Mobile platform - using variant from store: ${rootLanguageFolder}`, 0);
                } else {
                    logInfo('[getAppVersion] Mobile platform - variant not in store, reading from local language.json', 0);
                    // Try Directory.Data first, then assets
                    let languageData: any = null;
                    try {
                        languageData = await readJSONFileFromDataDirectory('language.json', false);
                        logInfo('[getAppVersion] Found language.json in Directory.Data', 0);
                    } catch (error) {
                        logInfo('[getAppVersion] Not in Directory.Data, trying assets', 0);
                    }
                    
                    if (!languageData) {
                        try {
                            const assetData = await fetch('assets/data/language.json');
                            languageData = await assetData.json();
                            logInfo('[getAppVersion] Found language.json in assets', 0);
                        } catch (error) {
                            logError(error, '[getAppVersion] Failed to read language.json from assets');
                        }
                    }
                    
                    if (languageData && languageData.locale) {
                        try {
                            // Decrypt the locale to access the variant
                            const decryptedLocale = decryptToJson(languageData.locale);
                            const localeKey = Object.keys(decryptedLocale)[0];
                            rootLanguageFolder = decryptedLocale[localeKey]?.language?.variant;
                            
                            if (rootLanguageFolder) {
                                logInfo(`[getAppVersion] Decrypted and extracted variant from language.json: ${rootLanguageFolder}`, 0);
                            } else {
                                // Ultimate fallback: use default variant
                                rootLanguageFolder = 'demopattern';
                                logInfo(`[getAppVersion] No variant found after decryption, using default: ${rootLanguageFolder}`, 0);
                            }
                        } catch (error) {
                            logError(error, '[getAppVersion] Error decrypting locale from language.json');
                            // Ultimate fallback: use default variant
                            rootLanguageFolder = 'demopattern';
                            logInfo(`[getAppVersion] Decryption failed, using default variant: ${rootLanguageFolder}`, 0);
                        }
                    } else {
                        // Ultimate fallback: use default variant
                        rootLanguageFolder = 'demopattern';
                        logInfo(`[getAppVersion] No language.json found, using default variant: ${rootLanguageFolder}`, 0);
                    }
                }
            }
            
            // ALWAYS download from S3 - this file is tiny and must be fresh
            logInfo(`[getAppVersion] Downloading from: ${rootLanguageFolder}/appVersion.txt`, 0);
            const result = await getFromS3(
                rootLanguageFolder, 
                `appVersion.txt?timestamp=${new Date().getTime()}`, 
                'text', 
                true
            );
            
            if (result.data && result.data.trim()) {
                const version = result.data.trim();
                logInfo(`[getAppVersion] Successfully downloaded version from S3: ${version}`, 0);
                return version;
            }
            
            // If download failed, return current db version as fallback
            const currentVersion = store.state.appVersion || 0;
            logInfo(`[getAppVersion] Download failed, using current db version: ${currentVersion}`, 0);
            return String(currentVersion);
        } catch (error: any) {
            logError(error, '[getAppVersion] Error downloading from S3');
            // Last resort: return current db version
            const currentVersion = store.state.appVersion || 0;
            logInfo(`[getAppVersion] Exception caught, using current db version: ${currentVersion}`, 0);
            return String(currentVersion);
        } finally {
            isAppVersionDownloading = false;
            pendingAppVersionPromise = null;
        }
    })();
    
    return pendingAppVersionPromise;
}

//Empty means don't reload - Smart version check that only downloads if needed
async function checkForNewVersion(): Promise<any> {
    let languageData = {};
    const isSecure = store.getters.isSecure();
    const secureOverride = store.state.secureUpdateOverride;
    console.log('[checkForNewVersion] isSecure=', isSecure, 'secureUpdateOverride=', secureOverride);
    // Secure app without network access: use local language only.
    if (isSecure && !store.getters.allowsInternetAccess()) {
        logInfo('[checkForNewVersion] Secure app - using local language only, no network', 0);
        await store.dispatch("setCheckForVersion", false);
        try {
            console.log('[checkForNewVersion] reading language.json from data directory');
            const localData = await readJSONFileFromDataDirectory('language.json', false);
            console.log('[checkForNewVersion] read done, keys=', localData ? Object.keys(localData).slice(0, 8) : 'null');
            if (localData && Object.keys(localData).length > 0) {
                return localData;
            }
        } catch (_e) {
            console.error('[checkForNewVersion] readJSONFileFromDataDirectory threw', _e);
            // Fall through to getLanguageFileData() which will also use local only for secure
        }
        console.log('[checkForNewVersion] calling getLanguageFileData()');
        return await getLanguageFileData();
    }
    if (store.getters.checkForVersion()) {
        logInfo('[Loading] 20% - check-version-start', 0);
        store.dispatch("setLoadingValue", { value: 0.2, slug: "check-version-start" });
        logInfo("[checkForNewVersion] Checking if version update is needed",1);
        
        // Check if we actually need to download by comparing versions
        try {
            // Get S3 version (tiny 10-byte file)
            const s3Version = await getAppVersion();
            const s3VersionNum = parseInt(s3Version);
            const currentDBVersion = store.state.appVersion || 0;
            
            logInfo(`[checkForNewVersion] S3 version: ${s3VersionNum}, DB version: ${currentDBVersion}`, 1);
            
            if (s3VersionNum <= currentDBVersion) {
                // No update needed
                logInfo('[checkForNewVersion] Local version is up to date, skipping download', 1);
                await store.dispatch("setCheckForVersion", false);
                return {}; // Return empty to indicate no new data
            }
            
            // Update available - check if we have it cached locally
            const deviceInfo = await Device.getInfo();
            if (deviceInfo.platform !== 'web') {
                const localData = await readJSONFileFromDataDirectory('language.json', false);
                if (localData && localData.appVersion && parseInt(localData.appVersion) === s3VersionNum) {
                    logInfo(`[checkForNewVersion] Local cache already has version ${s3VersionNum}, using it`, 1);
                    logInfo('[Loading] 20% - updating-language-from-local', 0);
                    store.dispatch("setLoadingValue", { value: 0.2, slug: "updating-language-from-local" });
                    // Note: App version will be updated when setLanguageData() processes this data and loads it into IndexedDB
                    await store.dispatch("setCheckForVersion", false);
                    return localData;
                }
            }
            
            // Update needed and not in cache - use cache + background download to avoid stall
            logInfo('[checkForNewVersion] Update available, using cache and scheduling background download to avoid stall', 1);
            
            const localData = await readJSONFileFromDataDirectory('language.json', false);
            if (localData && Object.keys(localData).length > 0) {
                // TEMPORARILY DISABLED: Background download disabled for debugging iPhone crash
                // Schedule background download
                // const livePath = deviceInfo.platform === "ios" 
                //     ? 'language_encrypted_iOSAppStore.json' 
                //     : 'language_encrypted_androidAppStore.json';
                // downloadLanguageInBackground(livePath, true);
                logInfo('[checkForNewVersion] BACKGROUND DOWNLOAD DISABLED FOR DEBUG', 0);
                
                await store.dispatch("setCheckForVersion", false);
                return localData; // Return cached data, update happens in background
            }
            
            // No cache available (rare) - must download
            logInfo("[checkForNewVersion] No cache available, must download from S3",1);
        languageData = await getLanguageFileData();
        await store.dispatch("setCheckForVersion", false);
            
        } catch (error: any) {
            logError(error, '[checkForNewVersion] Error checking version');
            // On error, try to use cached file
            const localData = await readJSONFileFromDataDirectory('language.json', false);
            if (localData && Object.keys(localData).length > 0) {
                await store.dispatch("setCheckForVersion", false);
                return localData;
            }
            // No cache, must try download
            logInfo("[checkForNewVersion] Error and no cache, attempting download",1);
            languageData = await getLanguageFileData();
            await store.dispatch("setCheckForVersion", false);
        }
    }
    try{
        //console.log(`[checkForNewVersion] language data keys: ${JSON.stringify(Object.keys(languageData))}`);
    }
    catch(e: any){
        const errObj = {
            location: "checkForNewVersion",
            message: "",
            stacktrace: (e.stack) ? e.stack : "No stack available",
            variables: new Map<string , string>
        } 
        errObj.variables.set("languageData", JSON.stringify(languageData));
        console.error(errObj);
    }
    return languageData;
}

function clearObjectStore(db: any, transaction: any, store: string) {
    if (db.objectStoreNames.contains(store)) {
        transaction.objectStore(store).clear();
    }
}

function createObjectStore(db: any, store: string, params: any) {
    if(!db.objectStoreNames.contains(store)) {
        return db.createObjectStore(store, params);
    }
}

/**
 * Extract app version from language data (top-level or first locale's application).
 * Avoids IndexedDB VersionError on restart when language file has nested structure (e.g. secure app).
 */
function getAppVersionFromLanguageData(languageData: any): number {
    if (!languageData || typeof languageData !== 'object') return 0;
    const top = languageData.appVersion;
    if (top != null && (typeof top === 'number' ? Number.isFinite(top) : /^\d+$/.test(String(top)))) {
        return typeof top === 'number' ? top : parseInt(String(top), 10);
    }
    const locale = languageData.locale || languageData;
    if (locale && typeof locale === 'object') {
        const firstKey = Object.keys(locale)[0];
        const app = firstKey && locale[firstKey]?.application;
        const v = app?.appVersion;
        if (v != null && (typeof v === 'number' ? Number.isFinite(v) : /^\d+$/.test(String(v)))) {
            return typeof v === 'number' ? v : parseInt(String(v), 10);
        }
    }
    return 0;
}

/**
 * Retrieves index db from local storage
 */
export async function getDb(): Promise<IDBDatabase> {
    console.log('[getDb] START');
    //getPreferences() called from router/index.ts is the first to reach this function
    let languageData: any;
    try {
        console.log('[getDb] calling checkForNewVersion()');
        languageData = await checkForNewVersion();
        console.log('[getDb] checkForNewVersion() done, keys=', languageData ? Object.keys(languageData).slice(0, 10) : 'null');
    } catch (e) {
        console.error('[getDb] checkForNewVersion() threw', e);
        throw e;
    }
    // Ensure platform is set when language was loaded via getDb path (e.g. router guard on update).
    // Otherwise store.platform stays '' and MainMenu hides secure-only items (cleanup, uninstall, update).
    await store.dispatch('setPlatform');
    // Return existing DB when already open and no version upgrade needed (avoids re-entrant open
    // from storeDefaults/getAllObjects inside loadDBInBackground which caused 80% hang on secure APK install)
    const fromData = getAppVersionFromLanguageData(languageData);
    const requestedVersion = Math.max(fromData, store.getters.getAppVersion() ?? 0);
    console.log('[getDb] fromData=', fromData, 'requestedVersion=', requestedVersion, 'DB=', !!DB, 'DB?.version=', DB?.version);
    if (DB && requestedVersion > 0 && requestedVersion <= DB.version) {
        return Promise.resolve(DB);
    }
    if (Object.keys(store.getters.getMinRequiredLoadData()).length === 0) {
        logInfo("[getDb] No min required load data, calling getMinRequiredDataForAppLoad", 1);
        try {
            console.log('[getDb] calling getMinRequiredDataForAppLoad()');
            await getMinRequiredDataForAppLoad(languageData);
            console.log('[getDb] getMinRequiredDataForAppLoad() done');
        } catch (e) {
            console.error('[getDb] getMinRequiredDataForAppLoad() threw', e);
            throw e;
        }
    }
    const { locale, quadrants} = store.getters.getMinRequiredLoadData();
    const appVersion = Math.max(getAppVersionFromLanguageData(languageData), store.getters.getAppVersion() ?? 0);
    // IndexedDB requires open version >= existing DB version; opening with 0 causes VersionError when DB already exists at higher version (e.g. after secure "Check for updates" + restart)
    const versionToOpen = appVersion >= 1 ? appVersion : 1;
    console.log('[getDb] opening IndexedDB', DB_NAME, 'version', versionToOpen);
    const request = window.indexedDB.open(DB_NAME, versionToOpen) ;

    return new Promise((resolve, reject) => {
        request.onerror = (e) => {
            const ev = (e as Event & { target?: { error?: unknown } }).target?.error;
            console.error('[getDb] IndexedDB onerror', ev, e);
            const msg = ev && typeof (ev as any)?.name === 'string' ? (ev as any).name + ': ' + (ev as any).message : 'IndexedDB open failed';
            reject(new Error(msg));
        };

        request.onsuccess = async (e) => {
            DB = (e as DataEvent).target.result;
            logInfo('[Loading] 80% - db-opened-load-background', 0);
            store.dispatch("setLoadingValue", { value: 0.8, slug: "db-opened-load-background" });
            logInfo("Debug (80%): getDB request onSuccess loadingDB in background",2);
            logInfo(`[getDb] : Langauge File Version: ${versionToOpen}`,2);
            await loadDBInBackground(locale, quadrants, String(versionToOpen));
            await runJourneySetKeyMigrationIfNeeded();
            resolve(DB);
        };

        request.onupgradeneeded = (e) => {

            const db = (e as DataEvent).target.result;
            const transaction = (e as DataEvent).target.transaction;
            store.dispatch("setVersionUpdated" , true);
            try {
                createObjectStore(db, "quadrant", {autoIncrement: true, keyPath: "id"});
                createObjectStore(db, "journeySet", {autoIncrement: true, keyPath: "id"});
                createObjectStore(db, "journey", {autoIncrement: true, keyPath: "id"});
                createObjectStore(db, "media", {autoIncrement: true, keyPath: "id"});
                createObjectStore(db, "resources" , {autoIncrement: true, keyPath: "id"});
                createObjectStore(db, "recentJourneys", {autoIncrement: false});
                const fileUris = createObjectStore(db, "fileUris", {autoIncrement: false});
                if (fileUris) {
                    fileUris.add([], 1);
                }

                for (const key of LANGUAGE_KEYS) {
                    createObjectStore(db, key, {autoIncrement: false});
                }

                const preferences = createObjectStore(db, "preferences", {autoIncrement: false});
                if (preferences) {
                    const preferencesObject = store.getters.getPreferences();
                    const defaultPreferences = encryptEntry(preferencesObject);
                    preferences.put(defaultPreferences, 1);
                }
            } catch (e){
                clearObjectStore(db, transaction, "quadrant");
                clearObjectStore(db, transaction, "journeySet");
                clearObjectStore(db, transaction, "journey");
                clearObjectStore(db, transaction, "media");
                clearObjectStore(db, transaction, "recentJourneys");
                clearObjectStore(db, transaction, "fileUris");
                for (const key of LANGUAGE_KEYS) {
                    clearObjectStore(db, transaction, key);
                }
            }
        };
    });
}


/**
 * Generic retriever for all objects of a type
 * @param storeName Primary Type
 * @param subType Sub Type filter
 * @param shouldDecrypt will the result set be decrypted
 * @param keyNameForArray result name
 */
export async function getAllObjects(storeName: string, subType?: string, shouldDecrypt = true, keyNameForArray = ""): Promise<[]> {
    const db = await getDb();

    return new Promise((resolve) => {
        const fields: [] = [];
        const trans = db.transaction([storeName], "readonly");
        trans.oncomplete = () => {
            resolve(fields);
        };
        const objectStore = trans.objectStore(storeName);

        objectStore.openCursor().onsuccess = (e) => {
            const cursor = (e as DataEvent).target.result;
            if (cursor) {
                const quadrants = store.getters.quadrantsToShow();
                if (
                    (storeName == "quadrant" && subType == "fields" && quadrants.includes(cursor.key)) ||
                    (!store.getters.showAllQuadrants() && storeName == "quadrant" && subType == "learnMore" && ["jf5"].includes(cursor.key)) ||
                    (!store.getters.showAllQuadrants() && storeName == "quadrant" && subType == "moreTopics" && ["jf6"].includes(cursor.key)) ||
                    storeName !== "quadrant"
                )
                    if (shouldDecrypt) {
                        try{
                            fields.push(decryptEntry(cursor.value, keyNameForArray ? cursor.value[keyNameForArray] : [], keyNameForArray) as never);
                        }catch(e: any){
                            const errObj = {
                                location: "GetAllObjects",
                                message: "",
                                stacktrace: (e.stack) ? e.stack : "No stack available",
                                variables: new Map<string , string>
                            } 
                            errObj.variables.set("shouldDecrypt", keyNameForArray);
                            errObj.variables.set("keyNameForArray", keyNameForArray);
                            errObj.variables.set("storeName", storeName);
                            logError(e , `Debug error: ${JSON.stringify(errObj)}`);
                        }
                    } else {
                        fields.push(cursor.value as never);
                    }
                cursor.continue();
            }
        };
    });
}

/**
 * Generic retriever for a single row
 * @param storeName Primary Type
 * @param key Primary Key
 * @param shouldDecrypt will the result be decrypted
 * @param keyNameForArray result name
 */
export async function getObject(storeName: string, key: any, shouldDecrypt = true, keyNameForArray = ""): Promise<any> {
    const db = await getDb();
    logInfo(`Debug: getObject storeName: ${storeName} , key: ${key}, shouldDecrypt: ${shouldDecrypt} , keyNameForArray: ${keyNameForArray}` , 2);
    if(!key || key == ""){
        logInfo("Debug: getObject no key supplied for store: " + storeName , 1);
    }
    return new Promise((resolve) => {
        const trans = db.transaction([storeName], "readonly");
        const store = trans.objectStore(storeName);
        const dbRequest = store.get(key);
        let returnValue: any;

        dbRequest.onsuccess = function() {
            // it's possible we get an undefined as a result when doing a lookup, let's make sure it exists before we decrypt
            
            if (shouldDecrypt && dbRequest.result) {
                try{
                    returnValue = decryptEntry(dbRequest.result, keyNameForArray ? dbRequest.result[keyNameForArray] : [], keyNameForArray);
                }catch(e: any){
                    const errObj = {
                        location: "GetObject",
                        message: e.message,
                        stacktrace: (e.stack) ? e.stack : "No stack available",
                        variables: new Map<string , string>
                    } 
                    errObj.variables.set("shouldDecrypt", keyNameForArray);
                    errObj.variables.set("keyNameForArray", keyNameForArray);
                    errObj.variables.set("storeName", storeName);
                    logError( e , `Debug error: ${JSON.stringify(errObj)}`);
                }
            } else {
                returnValue = dbRequest.result;
            }
        };

        trans.oncomplete = () => {
            resolve(returnValue);
        };
    });
}

/**
 * Generic save into Index DB
 * @param storeName Primary Type
 * @param object payload
 * @param keyNameForArray name of array
 */
export async function saveObject(storeName: string, object: any, keyNameForArray = "") {
    const db = await getDb();

    return new Promise<void>((resolve) => {
        const trans = db.transaction([storeName], "readwrite");
        trans.oncomplete = () => {
            resolve();
        };

        const store = trans.objectStore(storeName);
        const encryptedObject = encryptEntry(object, keyNameForArray ? object[keyNameForArray] : [], keyNameForArray);
        if (store.autoIncrement)
            store.put(encryptedObject);
        else
            store.put(encryptedObject, 1);
    });
}

/**
 * Generic update into Index Db
 * @param storeName Primary Type
 * @param key Primary Key
 * @param updateData payload
 * @param shouldEncrypt will the object be encrypted in index db
 * @param keyNameForArray name of array
 */
export async function update(storeName: string, key: any, updateData: any, shouldEncrypt = false, keyNameForArray = "") {
    const db = await getDb();
    return new Promise<void>((resolve) => {
        const trans = db.transaction([storeName], "readwrite");

        const store = trans.objectStore(storeName);

        store.openCursor().onsuccess = (e) => {
            const cursor = (e as DataEvent).target.result;
            if (cursor) {
                if (cursor.key === key) {
                    if (shouldEncrypt) {
                        cursor.update(encryptEntry(updateData, keyNameForArray ? updateData[keyNameForArray] : [], keyNameForArray));
                    } else {
                        cursor.update(updateData);
                    }
                }
                cursor.continue();
            }
        };
        trans.oncomplete = () => {
            resolve();
        };
    });
}

/**
 * Generic delete from Index Db
 * *NOTE index db does not really delete
 * 
 * Open bug: reported size of indexdb is non-intuitive, does not match the real size, and appears to grow on open.
 * https://bugs.chromium.org/p/chromium/issues/detail?id=795735 
 *
 * Open bug: orphaned blob data:
 * https://bugs.chromium.org/p/chromium/issues/detail?id=1071482
 * 
 * Tombstomb sweeper & subsequent compaction of index db will not run until 2 seconds after all connections are closed. We currently do not close index db. Before rewriting the data storage implementation we could test a 'Reclaim space button' to close db connections and see if compaction occurs. If successful then all db access needs to be rethought and wrapped in an improved handler
 * https://chromiumdash.appspot.com/commit/e35c5d8a89687ca85a154d572cb8cd332c5f430d
 * 
 * Deletes don't delete:
 * google/leveldb: Issue #783
 * https://github.com/google/leveldb/issues/783
 *
 * Allocation defaults for Chromium are 33% of the harddrive, a single app is capped at 20% of that. If all 20% of the space quota is consumed 'Quota exceeded' errors will likely crash the app or worse.
 * https://developer.mozilla.org/en-US/docs/Web/API/Storage_API/Storage_quotas_and_eviction_criteria
 * @param storeName Primary Type
 * @param key Primary Key
 */
export async function deleteObject(storeName: string, key: string | number) {
    const db = await getDb();
    
    return new Promise<void>((resolve) => {
        const trans = db.transaction([storeName], "readwrite");
        trans.oncomplete = () => {
            resolve();
        };

        const store = trans.objectStore(storeName);
        store.delete(key);
    });
}

const DB_VERSION_JOURNEY_SET_UPPERCASE_KEYS = 1.7;

/**
 * Returns all journey set store entries with their storage keys (for migration).
 * KeyPath is "id", so key may be lower/upper/mixed case.
 */
async function getJourneySetEntriesWithKeys(): Promise<{ key: string; value: JourneySet }[]> {
    const db = await getDb();
    const entries: { key: string; value: JourneySet }[] = [];
    return new Promise((resolve, reject) => {
        const trans = db.transaction(['journeySet'], 'readonly');
        const objectStore = trans.objectStore('journeySet');
        objectStore.openCursor().onsuccess = (e: Event) => {
            const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
                const key = cursor.key as string;
                try {
                    const value = decryptEntry(
                        cursor.value,
                        cursor.value?.resources ?? [],
                        'resources'
                    ) as JourneySet;
                    entries.push({ key, value });
                } catch (err) {
                    logError(err as Error, `[getJourneySetEntriesWithKeys] Failed to decrypt key ${key}`);
                }
                cursor.continue();
            } else {
                resolve(entries);
            }
        };
        trans.onerror = () => reject(trans.error);
    });
}

/**
 * Migrates journeySet store so each set exists only under its uppercase id.
 * - Both lower and upper key: overwrite upper with lower's content, delete lower.
 * - Only non-upper key: create upper row with that content, delete non-upper.
 * - Only upper key: no change.
 */
async function migrateJourneySetKeysToUppercase(): Promise<void> {
    logInfo('[migrateJourneySetKeysToUppercase] START', 0);
    const entries = await getJourneySetEntriesWithKeys();
    logInfo(`[migrateJourneySetKeysToUppercase] Loaded ${entries.length} journey set row(s) from store`, 0);
    if (entries.length === 0) {
        logInfo('[migrateJourneySetKeysToUppercase] No entries to migrate, exiting', 0);
        return;
    }
    for (const { key, value } of entries) {
        logInfo(
            `[migrateJourneySetKeysToUppercase] Raw entry: storageKey="${key}" value.id="${value?.id ?? '?'}" alreadyDownloaded=${value?.alreadyDownloaded ?? '?'} isDefaultSet=${value?.isDefaultSet ?? '?'} quadrantId=${value?.quadrantId ?? '?'}`,
            1
        );
    }
    const byNormalizedId = new Map<string, { key: string; value: JourneySet }[]>();
    for (const { key, value } of entries) {
        const normalizedId = key.toUpperCase();
        if (!byNormalizedId.has(normalizedId)) {
            byNormalizedId.set(normalizedId, []);
        }
        byNormalizedId.get(normalizedId)!.push({ key, value });
    }
    const needsMigration = [...byNormalizedId.entries()].filter(([, rows]) => rows.some((r) => r.key !== r.key.toUpperCase()));
    logInfo(
        `[migrateJourneySetKeysToUppercase] Grouped into ${byNormalizedId.size} normalized id(s), ${needsMigration.length} need migration (have non-uppercase keys)`,
        0
    );
    let groupsMigrated = 0;
    let keysDeleted = 0;
    for (const [normalizedId, rows] of byNormalizedId) {
        const upperRow = rows.find((r) => r.key === normalizedId);
        const lowerOrMixedRows = rows.filter((r) => r.key !== normalizedId);
        if (lowerOrMixedRows.length === 0) {
            logInfo(`[migrateJourneySetKeysToUppercase] Skip normalizedId="${normalizedId}" (already uppercase only)`, 2);
            continue;
        }
        const sourceRow = lowerOrMixedRows[0];
        const payload: JourneySet = { ...sourceRow.value, id: normalizedId };
        logInfo(
            `[migrateJourneySetKeysToUppercase] Migrating normalizedId="${normalizedId}": sourceKey="${sourceRow.key}" → uppercase, alreadyDownloaded=${payload.alreadyDownloaded} isDefaultSet=${payload.isDefaultSet} (upperRow exists=${!!upperRow})`,
            0
        );
        try {
            if (upperRow) {
                logInfo(`[migrateJourneySetKeysToUppercase] Updating existing row key="${normalizedId}"`, 1);
                await update('journeySet', normalizedId, payload, true, 'resources');
            } else {
                logInfo(`[migrateJourneySetKeysToUppercase] Creating new row key="${normalizedId}" (saveJourneySet)`, 1);
                await saveJourneySet(payload);
            }
            groupsMigrated += 1;
        } catch (err) {
            logError(err as Error, `[migrateJourneySetKeysToUppercase] FAILED to write normalizedId="${normalizedId}" — sourceKey="${sourceRow.key}" not deleted to avoid data loss`);
            continue;
        }
        for (const { key } of lowerOrMixedRows) {
            try {
                logInfo(`[migrateJourneySetKeysToUppercase] Deleting old key="${key}"`, 1);
                await deleteObject('journeySet', key);
                keysDeleted += 1;
            } catch (err) {
                logError(err as Error, `[migrateJourneySetKeysToUppercase] FAILED to delete old key="${key}" for normalizedId="${normalizedId}"`);
            }
        }
    }
    logInfo(
        `[migrateJourneySetKeysToUppercase] DONE: ${groupsMigrated} group(s) migrated, ${keysDeleted} old key(s) deleted`,
        0
    );
}

// One "restart to apply" toast per session so migration + bundle update don't both show (non-secure install).
let restartToastShownThisSession = false;
export function shouldShowRestartToast(): boolean {
    return !restartToastShownThisSession;
}
export function markRestartToastShown(): void {
    restartToastShownThisSession = true;
}

async function showRestartToApplyUpdateToast(): Promise<void> {
    if (!shouldShowRestartToast()) {
        logInfo('[showRestartToApplyUpdateToast] Skipping (restart toast already shown this session)', 1);
        return;
    }
    try {
        const { toastController } = await import('@ionic/vue');
        const messages = store.getters.getLocaleMessages();
        const toastMessage =
            messages?.application?.bundleUpdateAvailable ??
            'Update downloaded. Restart the app to install.';
        const toast = await toastController.create({
            message: toastMessage,
            duration: 3000,
            position: 'middle',
            cssClass: 'bundle-update-toast'
        });
        await toast.present();
        markRestartToastShown();
        logInfo('[showRestartToApplyUpdateToast] Shown restart-to-apply toast', 1);
    } catch (err) {
        logError(err as Error, '[showRestartToApplyUpdateToast] Failed to show toast');
    }
}

/**
 * If dbVersion < 1.7: runs or defers journey set key migration to uppercase.
 * - PWA and secure app: run migration immediately (no restart).
 * - Non-secure mobile: first time show toast and set migrationRestartRequired; after restart run migration.
 * Call only when DB is open.
 */
export async function runJourneySetKeyMigrationIfNeeded(): Promise<void> {
    const dbVersion = store.state.dbVersion ?? 0;
    const migrationRestartRequired = store.state.migrationRestartRequired === true;
    const deviceInfo = await Device.getInfo();
    const isPwa = deviceInfo.platform === 'web';
    logInfo(`[runJourneySetKeyMigrationIfNeeded] Check: dbVersion=${dbVersion}, migrationRestartRequired=${migrationRestartRequired}, isPwa=${isPwa} (platform=${deviceInfo.platform})`, 0);
    if (dbVersion >= DB_VERSION_JOURNEY_SET_UPPERCASE_KEYS) {
        logInfo('[runJourneySetKeyMigrationIfNeeded] Skipping (already at dbVersion 1.7)', 0);
        return;
    }
    if (isPwa) {
        logInfo('[runJourneySetKeyMigrationIfNeeded] PWA: running journey set key migration (no toast)', 0);
        try {
            await migrateJourneySetKeysToUppercase();
            await store.dispatch('setDbVersion', DB_VERSION_JOURNEY_SET_UPPERCASE_KEYS);
            await store.dispatch('setMigrationRestartRequired', false);
            logInfo('[runJourneySetKeyMigrationIfNeeded] Migration to dbVersion 1.7 completed', 0);
        } catch (err) {
            logError(err as Error, '[runJourneySetKeyMigrationIfNeeded] Migration failed, will retry on next load');
        }
        return;
    }
    if (store.getters.isSecure()) {
        logInfo('[runJourneySetKeyMigrationIfNeeded] Secure app: running journey set key migration (no restart)', 0);
        try {
            await migrateJourneySetKeysToUppercase();
            await store.dispatch('setDbVersion', DB_VERSION_JOURNEY_SET_UPPERCASE_KEYS);
            await store.dispatch('setMigrationRestartRequired', false);
            logInfo('[runJourneySetKeyMigrationIfNeeded] Migration to dbVersion 1.7 completed', 0);
        } catch (err) {
            logError(err as Error, '[runJourneySetKeyMigrationIfNeeded] Secure app migration failed, will retry on next load');
        }
        return;
    }
    if (!migrationRestartRequired) {
        await showRestartToApplyUpdateToast();
        await store.dispatch('setMigrationRestartRequired', true);
        logInfo('[runJourneySetKeyMigrationIfNeeded] Set migrationRestartRequired, user should restart', 0);
        return;
    }
    logInfo('[runJourneySetKeyMigrationIfNeeded] Running journey set key migration to uppercase', 0);
    try {
        await migrateJourneySetKeysToUppercase();
        await store.dispatch('setDbVersion', DB_VERSION_JOURNEY_SET_UPPERCASE_KEYS);
        await store.dispatch('setMigrationRestartRequired', false);
        logInfo('[runJourneySetKeyMigrationIfNeeded] Migration to dbVersion 1.7 completed', 0);
    } catch (err) {
        logError(err as Error, '[runJourneySetKeyMigrationIfNeeded] Migration failed, will retry on next restart');
    }
}

export async function deleteMedia(mediaId: string) {
    return deleteObject("media", mediaId);
}

export async function deleteQuadrant(quadrant: Quadrant) {
    return deleteObject("quadrant", quadrant.id);
}

/**
 * Deletes a journey set and its journeys.
 * - Not in manifest: full remove (journey set row + all journeys).
 * - In manifest: revert to Undownloaded — keep journey set row, remove journeys,
 *   set alreadyDownloaded = false; set deleted = true only if it's a default set
 *   so "check for missing default sets" won't re-download it.
 * @param journeySetId id of the journey set
 * @param inManifest true if the set is in the current manifest (metadata)
 * @param unavailable true when soft-deleting and set is no longer available on S3
 */
export async function deleteJourneySet(journeySetId: string, inManifest = false, unavailable = false) {
    const currentRecentJourneys = await getObject("recentJourneys", 1, false);
    const journeys = await getJourneys(false, journeySetId);
    const journeyIds = journeys.map(journey => journey.id);
    const newRecentJourneys = currentRecentJourneys.filter((val: string) => {
        return !journeyIds.includes(val);
    });
    update("recentJourneys", 1, [...newRecentJourneys]);
    await Promise.all(
        journeyIds.map(async (journeyId) => {
            await deleteObject('journey', journeyId);
        })
    );

    if (!inManifest) {
        await deleteObject("journeySet", journeySetId.toLowerCase());
        await deleteObject("journeySet", journeySetId.toUpperCase());
        return;
    }

    const journeySet = await getJourneySet(journeySetId);
    const keyFromData = journeySet.id;
    journeySet.alreadyDownloaded = false;
    journeySet.unavailableToDownload = unavailable;
    journeySet.deleted = journeySet.isDefaultSet ?? false;
    await updateJourneySet(journeySet);
    if (keyFromData !== journeySet.id) {
        await deleteObject("journeySet", keyFromData);
    }
}

/**
 * Returns all quadrants from index db
 * @param subType quadrant sub-type filter predicate
 * @param shouldDecrypt determines if the returned value is decrypted
 */
export async function getQuadrants(subType: string, shouldDecrypt = true): Promise<Quadrant[]> {
    return getAllObjects("quadrant", subType, shouldDecrypt);
}

/**
 * Returns a given quadrant
 * @param key quadrant primary key
 */
export async function getQuadrant(key: string): Promise<Quadrant> {
    return getObject("quadrant", key);
}

/**
 * Saves a quadrant
 * @param quadrant payload
 */
export async function saveQuadrant(quadrant: Quadrant) {
    saveObject("quadrant", quadrant);
}

/**
 * Updates a quadrant
 * @param quadrant payload
 */
export async function updateQuadrant(quadrant: Quadrant) {
    saveObject("quadrant", quadrant);
}

/**
 * Returns a given journey set, the mixing comes from mismatches in Admin Content Creator and Lambda drivers
 * @param key primary key
 */
export async function getJourneySet(key: string): Promise<JourneySet> {
    // Not ideal to check for both lowercase and uppercase, but the journey set ids in S3
    // kept getting mixed between uppercase and lowercase GUIDs
    // For backwards compatibility with apps in the field we're currently maintaining ids operating in either case
    //console.log(`Debug: getJourneySet key: ${key}`);
    const lowercaseId = await getObject('journeySet', key.toLowerCase(),true, "resources");
    const uppercaseId = await getObject('journeySet', key.toUpperCase(),true, "resources");
    return new Promise((resolve) => {
        resolve(lowercaseId || uppercaseId);
    });
}

/**
 * Update journeyset
 * @param journeySet payload
 */
const normalizeJourneySetId = (id: string) => id ? id.toUpperCase() : id;
const normalizeResourceId = (id?: string | number) => (id ?? '').toString().trim();
const RESOURCE_CHUNK_SIZE = 256 * 1024;

const shouldUseAssetResources = () => store.getters.isSecure() && !store.getters.allowsInternetAccess();
const isDownloadableResourceMime = (mimeType?: string) => {
    if (!mimeType) {
        return true;
    }
    return mimeType === MimeTypes.PDF || mimeType === MimeTypes.AUDIO;
};

function chunkString(value: string, size: number): string[] {
    if (!value) {
        return [];
    }
    const chunks: string[] = [];
    for (let i = 0; i < value.length; i += size) {
        chunks.push(value.slice(i, i + size));
    }
    return chunks;
}

function encryptResourceDataChunks(resourceData: string): string[] {
    const chunks = chunkString(resourceData, RESOURCE_CHUNK_SIZE);
    return chunks.map((chunk) => encrypt(JSON.stringify(chunk)).toString());
}

export interface ResourceFileRecord {
    id: string;
    version: number;
    content: string[];
    createdAt?: string;
    lastAccessed?: string;
}

async function getResourceRecord(resourceId: string): Promise<ResourceFileRecord | null> {
    if (!resourceId) {
        return null;
    }
    const db = await getDb();
    return new Promise((resolve) => {
        const trans = db.transaction(["resources"], "readonly");
        const store = trans.objectStore("resources");
        const dbRequest = store.get(resourceId);
        let returnValue: ResourceFileRecord | null = null;
        dbRequest.onsuccess = () => {
            const result = dbRequest.result as ResourceFileRecord | undefined;
            if (result && Array.isArray(result.content)) {
                returnValue = result;
            }
        };
        trans.oncomplete = () => {
            resolve(returnValue);
        };
    });
}

async function saveResourceRecord(record: ResourceFileRecord): Promise<void> {
    const db = await getDb();
    return new Promise((resolve) => {
        const trans = db.transaction(["resources"], "readwrite");
        const store = trans.objectStore("resources");
        store.put(record);
        trans.oncomplete = () => resolve();
    });
}

export async function saveResourceRecords(records: ResourceFileRecord[]): Promise<void> {
    if (!records || records.length === 0) {
        return;
    }
    for (const record of records) {
        if (record?.id && Array.isArray(record.content) && record.content.length > 0) {
            await saveResourceRecord(record);
        }
    }
}

async function downloadResourceFile(resourceId: string): Promise<ResourceFileRecord | null> {
    if (!resourceId) {
        return null;
    }
    try {
        const rootLanguageFolder = await getRootLanguageFolder();
        const resourceKey = `resources/${resourceId.toUpperCase()}.json?timestamp=${new Date().getTime()}`;
        const response = await getFromS3(rootLanguageFolder, resourceKey, "json");
        const data = response?.data;
        if (!data || !Array.isArray(data.content)) {
            return null;
        }
        return {
            id: resourceId,
            version: Number(data.version ?? 0),
            content: data.content,
            createdAt: new Date().toISOString(),
            lastAccessed: new Date().toISOString()
        };
    } catch (error: any) {
        logError(error, `[downloadResourceFile] Failed to download resource ${resourceId}`);
        return null;
    }
}

async function readResourceFileFromAssets(resourceId: string): Promise<ResourceFileRecord | null> {
    if (!resourceId) {
        return null;
    }
    try {
        const response = await fetch(`assets/data/resources/${resourceId.toUpperCase()}.json`);
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        if (!data || !Array.isArray(data.content)) {
            return null;
        }
        return {
            id: resourceId,
            version: Number(data.version ?? 0),
            content: data.content,
            createdAt: new Date().toISOString(),
            lastAccessed: new Date().toISOString()
        };
    } catch (error: any) {
        logError(error, `[readResourceFileFromAssets] Failed to read resource ${resourceId}`);
        return null;
    }
}

async function loadResourceFile(resourceId: string): Promise<ResourceFileRecord | null> {
    if (!resourceId) {
        return null;
    }
    if (shouldUseAssetResources()) {
        return await readResourceFileFromAssets(resourceId);
    }
    const isOnline = await isConnectedToInternet(false);
    if (!isOnline) {
        return null;
    }
    const downloaded = await downloadResourceFile(resourceId);
    if (downloaded) {
        return downloaded;
    }
    if (store.state.isSecure) {
        return await readResourceFileFromAssets(resourceId);
    }
    return null;
}

export async function getResourceFileRecordForExport(resourceId: string): Promise<ResourceFileRecord | null> {
    const normalizedId = normalizeResourceId(resourceId);
    if (!normalizedId) {
        return null;
    }
    const record = await getResourceRecord(normalizedId);
    if (record?.content?.length) {
        return record;
    }
    const loaded = await loadResourceFile(normalizedId);
    if (loaded?.content?.length) {
        await saveResourceRecord(loaded);
        return loaded;
    }
    return null;
}

function buildResourceRecordFromInline(resourceId: string, resourceData: string, version = 0): ResourceFileRecord {
    return {
        id: resourceId,
        version: Number(version ?? 0),
        content: encryptResourceDataChunks(resourceData),
        createdAt: new Date().toISOString(),
        lastAccessed: new Date().toISOString()
    };
}

async function ensureResourceAvailable(resource: AttachedResource): Promise<void> {
    const resourceId = normalizeResourceId(resource?.Id);
    if (!resourceId) {
        return;
    }
    if (!isDownloadableResourceMime(resource?.MimeType)) {
        return;
    }
    const desiredVersion = Number((resource as any)?.Version ?? 0);
    const existing = await getResourceRecord(resourceId);
    if (existing && typeof existing.version === "number" && existing.version >= desiredVersion) {
        return;
    }
    if (resource?.ResourceData) {
        const record = buildResourceRecordFromInline(resourceId, resource.ResourceData, desiredVersion);
        await saveResourceRecord(record);
        return;
    }
    const downloaded = await loadResourceFile(resourceId);
    if (downloaded) {
        await saveResourceRecord(downloaded);
    }
}

function collectTabAttachedResources(journeySet: JourneySet): AttachedResource[] {
    const resources: AttachedResource[] = [];
    const seen = new Set<string>();
    for (const journey of journeySet.journeys || []) {
        for (const tab of journey.tabs || []) {
            const attached = (tab as any)?.tabAttachedResources as AttachedResource[] | undefined;
            if (!attached || attached.length === 0) {
                continue;
            }
            for (const resource of attached) {
                const resourceId = normalizeResourceId(resource?.Id);
                if (!resourceId || seen.has(resourceId)) {
                    continue;
                }
                seen.add(resourceId);
                resources.push(resource);
            }
        }
    }
    return resources;
}

async function persistTabAttachedResources(journeySet: JourneySet): Promise<void> {
    const resources = collectTabAttachedResources(journeySet);
    if (resources.length === 0) {
        return;
    }
    for (const resource of resources) {
        await ensureResourceAvailable(resource);
    }
}

async function storeResourceFile(resourceId: string, resourceData?: string) {
    if (!resourceId || !resourceData) {
        return;
    }
    const existing = await getResourceRecord(resourceId);
    if (existing?.content?.length) {
        return;
    }
    const record = buildResourceRecordFromInline(resourceId, resourceData);
    await saveResourceRecord(record);
}

async function persistResources(resources?: AttachedResource[]) {
    if (!resources || resources.length === 0) {
        return;
    }
    for (const resource of resources) {
        await ensureResourceAvailable(resource);
    }
}

function stripResourceData(resource: AttachedResource): AttachedResource {
    const { ResourceData, ...rest } = resource;
    return { ...rest };
}

export async function getResourceDataById(resourceId: string, fallbackData?: string): Promise<string | null> {
    const normalizedId = normalizeResourceId(resourceId);
    if (!normalizedId) {
        return null;
    }
    let record = await getResourceRecord(normalizedId);
    if (!record) {
        const loaded = await loadResourceFile(normalizedId);
        if (loaded) {
            await saveResourceRecord(loaded);
            record = loaded;
        }
    }
    if (record?.content?.length) {
        let assembled = '';
        for (const chunk of record.content) {
            try {
                let decryptedChunk = decrypt(chunk).toString(CryptoJS.enc.Utf8);
                try {
                    const parsed = JSON.parse(decryptedChunk);
                    if (typeof parsed === 'string') {
                        decryptedChunk = parsed;
                    }
                } catch {
                    // Leave as-is if not JSON-wrapped
                }
                assembled += decryptedChunk;
            } catch (error) {
                logError(error, `[getResourceDataById] Failed to decrypt chunk for ${normalizedId}`);
            }
        }
        if (assembled) {
            await saveResourceRecord({
                ...record,
                lastAccessed: new Date().toISOString()
            });
            return assembled;
        }
    }
    const encrypted = await readTextFileFromDataDirectory(normalizedId);
    if (encrypted) {
        try {
            return decrypt(encrypted).toString(CryptoJS.enc.Utf8);
        } catch (error) {
            logError(error, `[getResourceDataById] Failed to decrypt resource ${normalizedId}`);
        }
    }
    if (fallbackData) {
        await storeResourceFile(normalizedId, fallbackData);
        return fallbackData;
    }
    return null;
}

export async function updateJourneySet(journeySet: JourneySet) {
    journeySet.id = normalizeJourneySetId(journeySet.id);
    update("journeySet", journeySet.id, journeySet, true);
}

/**
 * Returns all journeysets from index db
 * @param shouldDecrypt determines if result set is decrypted
 * @param whereClause predicate filter
 */
export async function getJourneySets(shouldDecrypt = true, whereClause = ""): Promise<JourneySet[]> {
    const journeySets = (await getAllObjects("journeySet", "", shouldDecrypt)) as JourneySet[];
    if (whereClause) {
        return journeySets
            .filter((journeySet) => journeySet.quadrantId === whereClause)
            .map((journeySet) => {
                try{
                    return decryptEntry(journeySet);
                } catch(e: any) {
                    const errObj = {
                        location: "GetJourneySets",
                        message: "",
                        stacktrace: (e.stack) ? e.stack : "No stack available",
                        variables: new Map<string , string>
                    };
                    errObj.variables.set("shouldDecrypt", shouldDecrypt.toString());
                    errObj.variables.set("whereClause", whereClause);
                    logError( e , `Debug error obj: ${JSON.stringify(errObj)}`);
                }
                return journeySet;
            });
    }
    return journeySets;
}

export async function getJourneySetsForCount(shouldDecrypt = false, whereClause = ""): Promise<JourneySet[]> {
    const journeySets = (await getAllObjects("journeySet", "", shouldDecrypt)) as JourneySet[];

    if (whereClause) {
        return journeySets
            .filter((journeySet) => journeySet.quadrantId === whereClause);
    }
    return journeySets;
}

/**
 * Save a journey set and all of it's sub-properties
 * @param journeySet payload
 */
export async function saveJourneySetAndData(journeySet: JourneySet) {
    journeySet.alreadyDownloaded = true;
    journeySet.updateAvailable = false;
    journeySet.deleted = false;
    if (journeySet.icon) {
        journeySet.iconId = uuidv4();
        await saveMedia({ id: journeySet.iconId, content: journeySet.icon || "" });
    }
    await persistTabAttachedResources(journeySet);
    await persistResources(journeySet.resources);
    if (journeySet.resources && journeySet.resources.length > 0) {
        journeySet.resources = journeySet.resources.map(stripResourceData);
    }
    await saveJourneySet(journeySet);

    for (const journey of journeySet.journeys || []) {
        if (journey.audioFile) {
            journey.audioFileId = uuidv4();
            await saveMedia({id: journey.audioFileId , content: journey.audioFile || "" });
        }
        //Force it into this format so it loads faster on the Journeys page
        await saveJourney({
            id: journey.id.toUpperCase(),
            name: journey.name,
            description: journey.description,
            audioFileId: journey.audioFileId,
            journeySetId: journeySet.id,
            isComplete: false,
            tabs: journey.tabs,
            order: journey.order
        });
    }

    
}


export async function saveJourneySet(journeySet: JourneySet) {
    journeySet.id = normalizeJourneySetId(journeySet.id);
    saveObject("journeySet", journeySet,"resources");
}

/**
 * Retrieve all journey sets
 * @param shouldDecrypt determines if result set is decrypted
 * @param whereClause filter predicate
 */
export async function getJourneys(shouldDecrypt = true, whereClause = ""): Promise<Journey[]> {
    const journeys = (await getAllObjects("journey", "", shouldDecrypt, "tabs")) as Journey[];
    if (whereClause) {
        return journeys
            .filter((journey) => journey.journeySetId === whereClause.toLowerCase() || journey.journeySetId === whereClause.toUpperCase() || journey.journeySetId === whereClause)
            .map((journeyToDecrypt) => {
                return decryptEntry(journeyToDecrypt, journeyToDecrypt.tabs, "tabs");
            }).sort((journey1, journey2) => journey1.order - journey2.order);
    }
    return journeys.sort((journey1, journey2) => journey1.order - journey2.order);
}

export async function getJourney(key: string): Promise<Journey> {
    return getObject("journey", key, true, "tabs");
}

export async function saveJourney(journey: Journey) {
    saveObject("journey", journey, "tabs");
}

export async function updateJourney(journey: Journey) {
    update("journey", journey.id, journey, true, "tabs");
}

export async function getLocaleMessages(): Promise<any> {
    const encryptedLanguage = await getObject("language", 1, false);
    
    const language = decryptToJson(encryptedLanguage);

    for (const key of LANGUAGE_KEYS) {
        if (key !== "language") {
            const encryptedObj = await getObject(key, 1, false);
            try{
                language.messages[key] = decryptToJson(encryptedObj);
            }catch(e: any){
                const errObj = {
                    location: "GetLocaleMessages",
                    message: "",
                    stacktrace: (e.stack) ? e.stack : "No stack available",
                    variables: new Map<string , string>
                } 
                errObj.variables.set("key", key);
                errObj.variables.set("encryptedObj", encryptedObj);
                logError( e , `Debug error: ${JSON.stringify(errObj)}`);
            }
        }
    }

    return language;

}

export async function getPreferences(): Promise<Preferences> {
    return getObject("preferences", 1);
}
export async function savePreferences(preferences: Preferences): Promise<void> {
    await update("preferences", 1, preferences, true);
    store.dispatch("setPreferences", preferences);
}

export async function getMediaItem(key: string): Promise<Media> {
    if (key && key.length > 0){
        return getObject("media", key);
    }else{
        return getObject("media" , "0");
    }
    
}

export async function getMedia(shouldDecrypt = true): Promise<Media[]> {
    return getAllObjects("media", "", shouldDecrypt);
}
export async function saveMedia(media: Media): Promise<void> {
    saveObject("media", media);
}

export async function getCategories(): Promise<Media[]> {
    return getAllObjects("category");
}
export async function saveCategory(category: Category): Promise<void> {
    saveObject("category", category);
}

/**
 * Recent journey sets are limited to 4, rolls off the oldest before adding a new
 * @param journeyId latest recent journey set
 */
export async function addRecentJourney(journeyId: string): Promise<void> {
    const recentJourneys = await getObject("recentJourneys", 1, false);
    const indexOfExisting = recentJourneys.findIndex((rjId: string) => rjId === journeyId);
    if (indexOfExisting > -1) {
        recentJourneys.unshift(journeyId);
        recentJourneys.splice(indexOfExisting+1, 1);
    }
    else {
        if (recentJourneys.length >= 4) {
            recentJourneys.pop();
        }
        recentJourneys.unshift(journeyId);
    }
    update("recentJourneys", 1, [...recentJourneys]);
}

/**
 * Retrieves recent journey sets and respective properties
 */
export async function getRecentJourneys(): Promise<Journey[]> {
    const recentJourneys = await getObject("recentJourneys", 1, false);
    const journeys: Journey[] = [];
    for (const journeyId of recentJourneys) {
        const journey = await getJourney(journeyId);
        const journeySet = await getJourneySet(journey?.journeySetId);
        if (journey && journeySet) {
            if(journeySet.iconId || journeySet.iconId.length > 0){ 
                const media = await getMediaItem(journeySet.iconId);
                journey.icon = getImageDataUri(media.content);
            }else{
                const logData = `Debug: Lesson id ${journeySet.id} has invalid icon id ${journeySet.iconId}` 
                logInfo(logData,1);
            }
            journeys.push(journey);
            
        }
    }
    return journeys;
}

export async function addFileUri(uri: string): Promise<void> {
    const uris = await getObject("fileUris", 1, false);
    uris.push(uri);
    await update("fileUris", 1, [...uris]);
}

export async function getFileUris(): Promise<string[]> {
    return getObject("fileUris", 1, false);
}

export async function clearFileUris(): Promise<void> {
    await update("fileUris", 1, []);
}

export interface AppError {
    location: string;
    variables?: Map<string, string>;
    message: string;
    stack?: string;
}

export interface Quadrant {
    id: string;
    name: string;
    altName?: string;
    // foreign key to media store
    iconId: string;
    icon?: string;
    altIcon?: string;
    journeySets?: JourneySet[];
}
export interface JourneySet {
    id: string;
    name: string;
    pdfName: string;
    // foreign key to media store
    iconId: string;
    icon?: string;
    // foreign key to journey store 
    quadrantId: string;
    // foreign key to category store
    categoryId?: string;
    isDefaultSet: boolean;
    tabContentImages: string[];
    tabImages: string[];
    journeys?: Journey[];
    order: number;
    published?: Date;
    locale?: string;
    alreadyDownloaded: boolean | undefined;
    numberOfJourneys: number;
    updateAvailable: boolean;
    deleted: boolean;
    unavailableToDownload: boolean | undefined;
    resources?: AttachedResource[];
}

export interface JourneySetToDisplay extends JourneySet {
    imageValue: string;
}

export interface Journey {
    id: string;
    name: string;
    description: string;
    isComplete: boolean;
    audioFileId?: string;
    audioFile?: string;
    videoFileId?: string;
    videoFile?: string;
    // foreign key to journey set store
    journeySetId: string;
    tabs: JourneyTab[];
    icon?: string;
    order: number;
}

export interface JourneyTab {
    tabName: string;
    contentText: string;
    isActive?: boolean;
    audioStartTime?: number;
    audioEndTime?: number;
    text1: string;
    text2: string;
    videoUrl?: string;
    videoLabel?: string;
    attachedResources?: any[];
    tabAttachedResources?: AttachedResource[];
    resourceLinks?: string;
    resourceListHeading?: string;
}

export interface Media {
    id: string;
    content: string;
}

export interface AttachedResource {
    Id: string;
    Name: string;
    ResourceData?: string;
    MimeType?: string;
    AppInstanceId?: string;
    appInstanceId?: string;
    Url?: string;
    Shareable?: boolean;
    InUseCount?: number;
    InUseCnt?: number;
    Version?: number;
    icon?: string;
    ResourceLabel?: string;
}

export interface ResourceMetadata {
    id: string;
    name: string;
    mimeType?: string;
    url?: string;
    shareable?: boolean;
    appInstanceId?: string;
    inUseCount?: number;
    resourceLabel?: string;
}

export interface Category {
    id: string;
    name: string;
}

export interface Preferences {
    shouldUseSafetyCalculator: boolean;
    shouldDisplayQuickExit: boolean;
    entrancePin: string;
    lastVisitedRoute: string;
    deletePin: string;
    shouldDisplayUnencryptedAlert: boolean;
    shouldDisplayInternetAlert: boolean;
    shouldDisplayAppSharedAlert: boolean;
    shouldSetPin: boolean;
    canAccessApp: boolean;
    simpleCategoryShapes: boolean;
    isRelease: boolean;
    manifest:string[];
    logLevel: number;
    allowAudioInnotes: boolean;
    allowNotes: boolean;
}

export interface Language {
    locale: string;
    messages: Record<string, unknown> | string;
}

interface DataEvent extends Event {
    target: any;
}
